// New Delhi, "Evening on the lawns of India Gate": a warm mid-October evening, before the winter haze, on the lawns of
// Kartavya Path about 250 m west of the gate, on the promenade along the southern boating canal, looking east. India Gate
// stands floodlit at the head of the lawns (it is lit from 7 to 9:30 every evening): the tall arch of buff Bharatpur
// sandstone on its low base of red stone, the inscription over the arch, the band with the dates, the frieze of imperial
// suns under the great cornice, the stepped attic and the shallow bowl on top, mirrored in the canal. Right of it and
// further back, because we stand south of the axis, Lutyens' stone canopy with the black granite statue of Netaji Subhas
// Chandra Bose (installed in 2022, where King George V stood until 1968), and the dome of Jaipur House far down the axis.
// Rashtrapati Bhavan is behind us, at the western end of the avenue, so it is not in the picture. Autos, DTC buses and
// cars circle the C-Hexagon one way (clockwise, so right to left here); an ice-cream tricycle rings along its footpath.
// On the far bank: jamun trees along the avenue, the canal-side lamp posts, a chai kiosk under its lit sign, families on
// the grass. On our side: the lamp posts in their line down the promenade, a bhutta (roast corn) seller fanning his
// coals, a kulfi cart, a balloon seller, a family on a sheet with light-up bunny ears, a couple strolling, a dog asleep,
// children shooting glowing toy copters into the sky, flying foxes, and flowering saptaparni trees whose cream blossom
// clusters fall at three depths.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, inEvent, cycle, hash, rng, bayer, hex,
  mix, makeCanvas, rect, makeHalo, drawHalo, streak, text, FONT3, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // the scene's 480x270 context, set in create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);
const ACCENT = '#ffa8a8';                             // sandstone rose (written out in the export block too)

/* ================= Layout: one camera for the whole scene ================= */
// We stand on the lawn 1.6 m above the grass, facing due east with a shift lens: the frame starts ~28° left of east and
// ends ~7° right of it, so every line parallel to the avenue runs to VPX on the horizon, and north-south lines (the
// gate's face, the C-Hexagon road) stay level. A point d metres ahead and L metres to our right (south), h metres up,
// lands at (PX, PY).
const VPX = 383, HZ = 172, F = 726, EYE = 1.6;
const PX = (d, L) => VPX + F * L / d;
const PY = (d, h = 0) => HZ + F * (EYE - h) / d;
const AXIS = -50;                                     // Kartavya Path's axis runs 50 m to our left (north)
// India Gate: front (west) face 254 m ahead, 23 m wide, 12 m deep, 42 m high; the south face shows as a sliver
const IG = { d: 254, dd: 12, w: 23, hgt: 42 };
IG.s = F / IG.d;                                      // px per metre on the front face
IG.x0 = PX(IG.d, AXIS - IG.w / 2); IG.x1 = PX(IG.d, AXIS + IG.w / 2); IG.cx = (IG.x0 + IG.x1) / 2;
IG.base = PY(IG.d);                                   // 176.6
const igY = h => IG.base - IG.s * h;                  // row of height h on the front face
const IG_TOP = Math.floor(igY(IG.hgt));               // the bowl's crown
// the canopy (chhatri), 150 m behind the gate on the axis, 22 m high; Jaipur House further down the axis
const CAN = { d: 404, hw: 4.5, hgt: 22.3 };
CAN.s = F / CAN.d; CAN.cx = PX(CAN.d, AXIS); CAN.base = PY(CAN.d);
const JH = { d: 640 };
JH.s = F / JH.d; JH.cx = PX(JH.d, AXIS); JH.base = PY(JH.d);
// the C-Hexagon ring road between us and the gate (98..122 m), one way, right to left; the canal ends at 90 m
const ROAD = { d0: 98, d1: 122 };
const LANES = [{ d: 114, base: 182 }, { d: 103, base: 183 }];          // far lane first
const CANAL = { Ln: -3, Lf: -16, end: 90, dep: 0.3 };                   // banks (L), end (d), water 0.3 m below the lawn
const WTOP = 187;                                                        // first row of water (the canal's end)
// row y (pixel centre) → the far bank's waterline x and the near bank's edge x
const farWater = y => VPX + CANAL.Lf * (y + 0.5 - HZ) / (EYE + CANAL.dep);
const nearEdge = y => VPX + CANAL.Ln * (y + 0.5 - HZ) / EYE;
// the chai kiosk on the far bank at the canal's head, its lit sign on posts above the roof (the site's billboard)
const KIOSK = { d: 73, L: -20.4, hw: 1.85, hgt: 2.8 };
KIOSK.s = F / KIOSK.d; KIOSK.cx = PX(KIOSK.d, KIOSK.L); KIOSK.base = Math.round(PY(KIOSK.d));
const BB = { x: 157, y: 128, w: 46, h: 20 };          // the sign's face (the site paints its pitch here)
// lamp posts: our promenade (by the water), the far bank's walk, the C-Hexagon's tall road lamps
const NEAR_LAMPS = [30, 46, 66, 88].map(d => ({ d, L: -2.2, hgt: 4.5 }));
const FAR_LAMPS = [36, 49].map(d => ({ d, L: -17.2, hgt: 4.5 }));
const ROAD_LAMPS = [{ x: 18 }, { x: 326 }, { x: 414 }];
const ROAD_LAMP = { d: 97, hgt: 9 };
const JAMUNS = [{ d: 80, L: -30, r: 3.8, seed: 3 }, { d: 67, L: -30.5, r: 4, seed: 2 }, { d: 55, L: -30, r: 4.3, seed: 1 }];   // far to near
// our side: the saptaparni trees, vendors, the family, the dog
const SAPTA = [{ d: 58, L: 5.2, seed: 7 }, { d: 31, L: 3.1, seed: 5 }];  // far one first
const BHUTTA = { d: 28.5, L: -0.2 };                 // the roast-corn cart on the promenade
const KULFI = { d: 62, L: 1.0 };                     // the kulfi cart at the lawn's edge
const BALLOON = { d: 40, L: 2.4 };                   // the balloon (and glowing copter) seller
const FAMILY = { d: 23, L: 0.4 };                    // a family on a sheet, the sheet runs to L + 2.5
const COUPLE = { d: 26, L: -2.7 };                  // a couple sitting on the canal's edge
const DOG = { d: 19.5, L: -1.9 };

/* ================= Small pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    get(i, j) { const o = (j * w + i) * 4; return [d[o], d[o + 1], d[o + 2], d[o + 3]]; },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** '#rrggbb' between two colours (for pix buffers; core's mix() gives an rgb() string for fillStyle) */
const mixHex = (a, b, t) => '#' + hex(a).map((v, i) => Math.round(lerp(v, hex(b)[i], t)).toString(16).padStart(2, '0')).join('');
/** ordered-dithered pick from a colour ramp, v in 0..1 */
function pick(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, j)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** posterised pick: flat bands with a narrow dither seam (painted signs, not gradients) */
function pickFlat(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * 0.3) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only) */
function vnoise(x, y, s) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const a = hash(xi, yi, s), b = hash(xi + 1, yi, s), c = hash(xi, yi + 1, s), d = hash(xi + 1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** crop a pix buffer to its opaque bounding box (build time); draw the result with blit() */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0, w: 1, h: 1 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}
const blit = o => ctx.drawImage(o.c, o.x, o.y);
/** a sprite from rows of palette keys ('.' = transparent) */
function sprite(rows, pal) {
  const p = pix(rows[0].length, rows.length);
  rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] !== '.') p.set(i, j, pal[row[i]]); });
  return p.done();
}
/** a sprite and its mirror image (for things that move either way) */
function spritePair(rows, pal) {
  const right = sprite(rows, pal), [left, lx] = makeCanvas(right.width, right.height);
  lx.translate(right.width, 0); lx.scale(-1, 1); lx.drawImage(right, 0, 0);
  return { right, left, w: right.width, h: right.height };
}
/** FONT3 text into a pix buffer, at scale sc */
function textP(p, s, x, y, col, sc = 1) {
  let cx = x;
  for (const ch of s) { const g = FONT3[ch]; if (g) g.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') p.fill(cx + i * sc, y + j * sc, sc, sc, col); }); cx += 4 * sc; }
}
/** filled disc into a pix buffer; colFn(dx, dy, x, y) or a colour */
function disc(p, cx, cy, r, colFn) {
  for (let y = Math.floor(cy - r); y <= Math.ceil(cy + r); y++) for (let x = Math.floor(cx - r); x <= Math.ceil(cx + r); x++) {
    const dx = (x - cx) / r, dy = (y - cy) / r;
    if (dx * dx + dy * dy <= 1) p.set(x, y, typeof colFn === 'function' ? colFn(dx, dy, x, y) : colFn);
  }
}
/** flattened dithered ellipse of light (pools on the ground), drawn additively */
function makePool(rx, ry, col, maxA) {
  const w = rx * 2 + 1, h = ry * 2 + 1, p = pix(w, h), c = hex(col);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const d = Math.hypot((x - rx) / rx, (y - ry) / ry);
    if (d >= 1) continue;
    const q = Math.floor(Math.pow(1 - d, 1.5) * 6 + bayer(x, y)) / 6;
    if (q > 0) p.set(x, y, c, Math.round(q * maxA * 255));
  }
  return { c: p.done(), rx, ry };
}
function drawPool(pl, x, y, a) {
  if (a <= 0.004) return;
  ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = Math.min(1, a);
  ctx.drawImage(pl.c, Math.round(x) - pl.rx, Math.round(y) - pl.ry);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
}
/** a flicker level: 1, with stutters inside the event windows */
function flick(t, ev, salt) {
  if (!inEvent(t, ev)) return 1;
  const hv = hash(salt, Math.floor(t * 20), 3);
  return hv > 0.45 ? 1 : hv > 0.2 ? 0.45 : 0.1;
}
/** a line of pixels into a pix buffer */
function line(p, x0, y0, x1, y1, col) {
  const n = Math.max(1, Math.ceil(Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0))));
  for (let k = 0; k <= n; k++) p.set(lerp(x0, x1, k / n), lerp(y0, y1, k / n), typeof col === 'function' ? col(k / n) : col);
}
/** the light a world point (d, L) gets from a list of lamps [{ d, L, r, k }], radius r in metres */
function worldLight(d, L, lamps) {
  let v = 0;
  for (const l of lamps) { const q = 1 - Math.hypot(d - l.d, L - l.L) / l.r; if (q > 0) v += l.k * q * q; }
  return v;
}

/* ================= Sky: a warm October night facing east, the city's glow low down ================= */
const SKY = ['#05071a', '#070a20', '#0a0e28', '#0e1330', '#131838', '#191d40', '#212246', '#2a264c', '#34294e', '#3f2d50',
  '#4b304e', '#58344c', '#673948', '#763f44', '#864842', '#94523e', '#a25e3c'];
function renderSky() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 178), 1.7) * 0.8;
    v += Math.max(0, 1 - Math.hypot((i - IG.cx) / 170, (y - 176) / 72)) * 0.22;           // the floodlit gate in the haze
    v += Math.max(0, 1 - Math.hypot((i - 460) / 120, (y - 180) / 40)) * 0.08;              // the city to the south-east
    v += (vnoise(i / 40, y / 9, 11) - 0.5) * 0.05 * smooth(60, 150, y);                    // a faint smog band
    p.set(i, y, pick(SKY, v, i, y));
  }
  return p.done();
}
// few stars through the city's glow: most high up, a couple of bright ones
const STARS = [];
{
  const r = rng(21);
  for (let k = 0; k < 900 && STARS.length < 84; k++) {
    const x = Math.floor(r() * W), y = 1 + Math.floor(r() * 120);
    if (r() > 1.15 - y / 110) continue;                          // thinner towards the horizon glow
    const bright = STARS.length < 3;
    STARS.push({ x, y, k: 17 + Math.floor(r() * 50), ph: r(), b: bright ? 0.95 : 0.2 + r() * 0.4 * (1 - y / 160), bright,
      col: r() < 0.3 ? '#ffe6c8' : r() < 0.55 ? '#d6e2ff' : '#f2eeff' });
  }
  STARS[0].x = 176; STARS[0].y = 24; STARS[1].x = 318; STARS[1].y = 58; STARS[2].x = 262; STARS[2].y = 12;
}
/** thin streaks of smoky haze, lit from below by the city; they drift once across per loop */
function renderHaze() {
  const h = 60, p = pix(W, h), r = rng(33), puffs = [];
  for (let k = 0; k < 9; k++) puffs.push({ x: r() * W, y: 6 + r() * 48, rx: 30 + r() * 60, ry: 1.2 + r() * 1.8 });
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = -9;
    for (const q of puffs) { let dx = x - q.x; dx -= Math.round(dx / W) * W; v = Math.max(v, 1 - (dx / q.rx) ** 2 - ((y - q.y) / q.ry) ** 2); }
    v += 0.3 * Math.sin(TAU * 6 * x / W + y * 0.7) * Math.sin(TAU * 2 * x / W);
    if (v <= bayer(x, y) * 0.6) continue;
    p.set(x, y, y > 40 ? (v > 0.55 ? '#7a4a4c' : '#643e4a') : v > 0.55 ? '#4a3450' : '#3a2c4c');
  }
  return p.done();
}
const HAZE_Y = 96;

/* a jet on its way down into Delhi: red and green lamps, a white strobe, descending right to left */
const PLANES = [{ t0: 30, t1: 104, x0: 470, x1: 118, y0: 20, y1: 44 }, { t0: 150, t1: 214, x0: 136, x1: 300, y0: 8, y1: 6 }];
function drawPlanes(t) {
  for (const P of PLANES) {
    if (t < P.t0 || t >= P.t1) continue;
    const s = (t - P.t0) / (P.t1 - P.t0), x = Math.round(lerp(P.x0, P.x1, s)), y = Math.round(lerp(P.y0, P.y1, s));
    const dir = Math.sign(P.x1 - P.x0);
    rect(ctx, x - 2, y, 5, 1, '#2a2a4a'); rect(ctx, x, y - 1, 1, 1, '#2a2a4a');
    rect(ctx, x - dir * 2, y, 1, 1, '#ff4a4a'); rect(ctx, x + dir * 2, y, 1, 1, '#4aff8a');
    if (P.y0 > 15) { rect(ctx, x + dir * 3, y, 1, 1, '#fff6d8'); halo(HL.landing, x + dir * 3, y, 0.7); }   // landing light
    const f = frac(t * 0.9);
    if (f < 0.06 || (f > 0.15 && f < 0.2)) { rect(ctx, x, y - 1, 1, 1, '#ffffff'); halo(HL.strobe, x, y - 1, 0.9); }
  }
}

/* flying foxes (Indian fruit bats) crossing over the lawns to the fig and jamun trees: slow, deep wingbeats */
const BATS = [
  { t0: 40, dur: 13, x0: 500, x1: -20, y0: 36, y1: 58, ph: 0.1 }, { t0: 41.2, dur: 14, x0: 506, x1: -16, y0: 48, y1: 64, ph: 0.6 },
  { t0: 124, dur: 12, x0: -20, x1: 500, y0: 70, y1: 30, ph: 0.3 }, { t0: 196, dur: 15, x0: 500, x1: -20, y0: 22, y1: 40, ph: 0.8 },
];
const BAT_F = [['#.....#', '##...##', '.#####.', '...#...'], ['.......', '###.###', '.#####.', '...#...'], ['.......', '.......', '#######', '.#.#.#.']];
function drawBats(t) {
  for (const b of BATS) {
    const s = (t - b.t0) / b.dur;
    if (s < 0 || s >= 1) continue;
    const x = Math.round(lerp(b.x0, b.x1, s)), y = Math.round(lerp(b.y0, b.y1, s) + Math.sin(s * 9 + b.ph * 6) * 3);
    const f = frac(t * 1.7 + b.ph), fr = f < 0.4 ? 0 : f < 0.55 ? 1 : f < 0.85 ? 2 : 1;
    ctx.fillStyle = '#0c0a14';
    BAT_F[fr].forEach((row, j) => { for (let q = 0; q < 7; q++) if (row[q] === '#') ctx.fillRect(x - 3 + q, y - 1 + j, 1, 1); });
    ctx.fillStyle = '#3a2830'; ctx.fillRect(x, y + (fr === 2 ? 1 : 2), 1, 1);                  // the warm belly, lit from below
  }
}

/* ================= Glowing toy copters: shot up with a rubber band, they spin down slowly, blue and pink ================= */
// far ones from the lawns round the gate (seen whole), near ones from our side (they fly out of the top and drift back)
const COPTERS = [
  { t0: 6, x0: 196, y0: 178, top: 96, dx: -10, col: 0, far: true }, { t0: 22.5, x0: 318, y0: 177, top: 104, dx: 12, col: 1, far: true },
  { t0: 47, x0: 262, y0: 178, top: 84, dx: 8, col: 0, far: true }, { t0: 71, x0: 214, y0: 178, top: 110, dx: -6, col: 2, far: true },
  { t0: 96, x0: 330, y0: 176, top: 92, dx: -14, col: 0, far: true }, { t0: 118, x0: 190, y0: 178, top: 100, dx: 10, col: 1, far: true },
  { t0: 143, x0: 280, y0: 177, top: 88, dx: -9, col: 0, far: true }, { t0: 168, x0: 236, y0: 178, top: 106, dx: 6, col: 2, far: true },
  { t0: 190, x0: 306, y0: 177, top: 98, dx: 11, col: 0, far: true }, { t0: 214, x0: 204, y0: 178, top: 90, dx: -8, col: 1, far: true },
  { t0: 13, x0: 404, y0: 196, top: -30, dx: -34, col: 0, far: false, land: 214 }, { t0: 88, x0: 372, y0: 200, top: -30, dx: 30, col: 1, far: false, land: 219 },
  { t0: 158, x0: 418, y0: 194, top: -30, dx: -40, col: 0, far: false, land: 216 },
];
const COP_COL = [['#6ad8ff', '#2a8cff'], ['#ff8ae8', '#e03ab8'], ['#9aff8a', '#30c860']];
const COP_UP = 0.9, COP_DOWN = 7.5;                   // seconds up, seconds spinning down
/** where a copter is at t: { x, y, spin } or null */
function copterAt(c, t) {
  const s = t - c.t0;
  if (s < 0) return null;
  if (s < COP_UP) { const u = s / COP_UP, e = 1 - (1 - u) * (1 - u); return { x: c.x0 + c.dx * 0.15 * u, y: lerp(c.y0, c.top, e), up: true }; }
  const down = c.far ? COP_DOWN : COP_DOWN * 1.2, u = (s - COP_UP) / down;
  if (u >= 1) return null;
  const yEnd = c.far ? c.y0 : c.land;
  return { x: c.x0 + c.dx * (0.15 + 0.85 * u) + Math.sin(u * 17 + c.t0) * 3, y: lerp(c.top, yEnd, u), up: false };
}
function drawCopters(t, far) {
  for (const c of COPTERS) {
    if (c.far !== far) continue;
    const q = copterAt(c, t);
    if (!q || q.y < -2) continue;
    const [lit, deep] = COP_COL[c.col], x = Math.round(q.x), y = Math.round(q.y);
    if (q.up) {                                                          // a streak on the way up
      ctx.fillStyle = lit; ctx.globalAlpha = 0.9; ctx.fillRect(x, y, 1, far ? 3 : 5); ctx.globalAlpha = 1;
    } else {
      const sp = Math.floor(t * 14 + c.t0) & 3;                          // the spinning blades flash left and right
      rect(ctx, x, y, 1, 1, '#ffffff');
      if (!far) rect(ctx, x + (sp < 2 ? -1 : 1), y, 1, 1, lit);
      rect(ctx, x + [-2, -1, 1, 2][sp], y - (far ? 0 : 1), 1, 1, deep);
    }
    halo(far ? HL.copF : HL.cop[c.col], x, y, q.up ? 0.6 : 0.9);
  }
}

/* ================= Far away: the C-Hexagon's trees, Jaipur House's dome down the axis, city lights ================= */
const TREE_FAR = ['#070810', '#0b0c16', '#10111c', '#161622', '#1e1c28', '#2a2430', '#3a2e34'];
function renderFar() {
  const p = pix(W, H), lights = [];
  const ridge = x => {                                              // tree tops, taller on the hexagon's north and south sides
    const side = smooth(200, 110, x) * 16 + smooth(300, 400, x) * 12;
    return HZ - 9 - side - 5 * vnoise(x / 7, 0, 41) - 4 * vnoise(x / 2.6, 0, 42) - (hash(x >> 2, 0, 43) < 0.2 ? 2 : 0);
  };
  for (let x = 0; x < W; x++) {
    const top = Math.round(ridge(x));
    for (let y = top; y < 178; y++) {
      const glow = Math.max(0, 1 - Math.hypot((x - IG.cx) / 110, (y - 174) / 16)) * 0.55;   // the gate's floodlights on the trees
      let v = 0.18 + (y - top) / 40 * 0.25 + glow + (vnoise(x / 2.2, y / 2, 44) - 0.5) * 0.18;
      if (y === top || (y === top + 1 && hash(x, y, 45) < 0.5)) v += 0.14;                   // a cool rim from the sky
      p.set(x, y, pick(TREE_FAR, v, x, y));
    }
  }
  // Jaipur House (the National Gallery) at the end of the axis: a pale dome on its drum, the wings behind the trees
  const s = JH.s, cx = JH.cx, b = JH.base;
  for (let y = Math.floor(b - 23 * s); y < Math.ceil(b); y++) for (let x = Math.floor(cx - 26 * s); x <= Math.ceil(cx + 26 * s); x++) {
    const u = (x + 0.5 - cx) / s, h = (b - (y + 0.5)) / s, au = Math.abs(u);
    let v = -1;
    if (h < 11 && au < 24) v = 0.32 + (h < 1 ? -0.1 : 0) + (Math.abs(h - 10.4) < 0.5 ? 0.12 : 0);
    else if (h >= 11 && h < 15 && au < 6.2) v = 0.36 + (h > 14.4 ? 0.12 : 0);
    else if (h >= 15 && h < 22.5) { const k = (h - 15) / 7.5; if (au < 5.6 * Math.sqrt(Math.max(0, 1 - k * k))) v = 0.42 - au * 0.03 + (1 - k) * 0.08; }
    else if (h >= 22.5 && h < 23.5 && au < 0.6) v = 0.5;
    if (v < 0) continue;
    if (h < 11 && hash(x, y, 46) < 0.1) v -= 0.1;
    p.set(x, y, pick(['#1a1620', '#262028', '#342a30', '#463838', '#5a4840', '#6e5a4c'], v, x, y));
  }
  for (let k = 0; k < 10; k++) { const u = -20 + k * 4.4; if (Math.abs(u) < 7) continue; lights.push({ x: Math.round(cx + u * s), y: Math.round(b - 5 * s), id: k }); }
  // street lamps through the trees, far off
  const r = rng(47);
  for (let k = 0; k < 26; k++) {
    const x = Math.floor(r() * W), y = Math.round(ridge(x) + 4 + r() * (174 - ridge(x) - 4));
    if (Math.abs(x - IG.cx) < 50 || Math.abs(x - CAN.cx) < 14) continue;
    lights.push({ x, y, id: 100 + k, warm: r() < 0.7 });
  }
  // two far towers on the skyline across the river, with red aviation lamps
  for (const [x, top] of [[58, 150], [430, 152]]) for (let y = top; y < Math.round(ridge(x)) + 2; y++) p.set(x, y, '#141420');
  return { ...trim(p), lights };
}
const AVIATION = [{ x: 58, y: 149, k: 0 }, { x: 430, y: 151, k: 0.5 }];

/* ================= The canopy: Lutyens' open pavilion on four piers, a dome and a finial; Netaji's statue inside ================= */
const TAN = ['#26160f', '#3e2415', '#5a341b', '#7a4922', '#9a602a', '#b87a36', '#d09446', '#e2ae5c', '#f0c67a', '#f8dc9c', '#fdeec4'];
const REDST = ['#2a1210', '#431b16', '#5e241b', '#7a3022', '#963e2a', '#b04e34', '#c46442', '#d27c56'];
const GRANITE = ['#08080e', '#101018', '#1a1a24', '#26242e', '#36323a', '#4a4046', '#62544e'];
function renderCanopy() {
  const p = pix(W, H), s = CAN.s, top = Math.floor(PY(CAN.d, CAN.hgt));
  for (let y = top - 1; y < Math.ceil(CAN.base) + 1; y++) for (let x = Math.floor(CAN.cx - 7 * s); x <= Math.ceil(CAN.cx + 7 * s); x++) {
    const u = (x + 0.5 - CAN.cx) / s, h = (CAN.base - (y + 0.5)) / s, au = Math.abs(u);
    if (h < 0) continue;
    const lit = 0.5 + 0.18 * (1 - h / 22) + (u < 0 ? 0.04 : -0.04);
    let v = -1, ramp = TAN;
    if (h < 1.6) { if (au < 5.4) { v = 0.46 + (h > 1.2 ? 0.1 : 0); ramp = REDST; } }
    else if (h < 14.6) {
      if (au < 4.5 && au >= 3.0) v = lit + (au > 4.2 ? 0.08 : au < 3.3 ? -0.12 : 0) + (Math.abs(h - 13.8) < 0.4 ? 0.1 : 0);   // twin columns
      else if (au < 3.0) {
        // the statue on its pedestal, black granite lit by spotlights from the front
        const ped = h < 4.4 && au < 1.35;
        const legs = h >= 4.4 && h < 8.4 && au < 0.95 && !(au < 0.18 && h < 7.6);
        const coat = h >= 8.4 && h < 11.2 && au < (h > 10.7 ? 1.4 : 1.2 + (h - 8.4) * 0.05);
        const head = h >= 11.2 && h < 12.4 && au < 0.52, cap = h >= 12.4 && h < 12.9 && au < 0.6;
        if (ped) { v = 0.5 + (h > 4.0 ? 0.1 : 0); ramp = REDST; }
        else if (legs || coat || head || cap) {
          v = 0.22 + 0.2 * (1 - (h - 4.4) / 8.5) + (u < -0.3 ? 0.08 : 0) + (head ? 0.1 : 0) + (coat && au < 0.25 ? 0.06 : 0);
          ramp = GRANITE;
        } else if (h > 13.2) v = lit + 0.12;                                          // the lit ceiling under the lintel
        else if (au > 2.5) v = lit - 0.28;                                            // the back piers, in shade
      }
    } else if (h < 16.4) { if (au < 5.0) v = lit + (h < 14.9 ? 0.2 : h > 16.1 ? -0.1 : 0) + (x % 2 && h > 15.2 && h < 15.7 ? -0.1 : 0); }
    else if (h < 16.9) { if (au < 5.5) v = lit + 0.22; }
    else if (h < 21) { const k = (h - 16.9) / 4.1; if (au < 4.7 * Math.sqrt(Math.max(0, 1 - k * k))) v = lit - 0.05 + (1 - k) * 0.08 - au * 0.03 + (Math.abs(u + 1.4) < 0.5 ? 0.08 : 0); }
    else if (h < CAN.hgt) { if (au < (h < 21.5 ? 0.9 : 0.34)) v = lit + 0.1; }
    if (v < 0) continue;
    p.set(x, y, pick(ramp, v * (ramp === TAN ? 0.86 : 1), x, y));
  }
  return trim(p);
}

/* ================= India Gate: buff Bharatpur sandstone on red, floodlit from the lawns ================= */
/** the zone of the front face at u metres from its left edge and h metres up, or null outside the silhouette */
function igFront(u, h) {
  if (h < 0 || h >= IG.hgt) return null;
  if (h < 1.4) return u >= -0.6 && u <= 23.6 ? 'plat' : null;
  const au = Math.abs(u - 11.5);
  if (h < 25) {
    if (u < 0 || u > 23) return null;
    return au < 4.55 && (h < 15.5 || Math.hypot(au, h - 15.5) < 4.55) ? 'arch' : 'wall';
  }
  if (h < 30) return u >= 0.35 && u <= 22.65 ? 'attic' : null;
  if (h < 32.4) return u >= 0.2 && u <= 22.8 ? 'frieze' : null;
  if (h < 34.4) return u >= -0.6 && u <= 23.6 ? 'cornice' : null;
  if (h < 36.8) return u >= 3.2 && u <= 19.8 ? 'stageA' : null;
  if (h < 38.6) return u >= 5.3 && u <= 17.7 ? 'stageB' : null;
  if (h < 39.1) return au <= 5.5 ? 'lip' : null;
  const k = (h - 38.6) / 3.4;
  return au <= 5.1 * Math.sqrt(Math.max(0, 1 - k * k)) ? 'dome' : null;
}
/** carved marks that read as lettering from afar but spell nothing: cell c of a group, pixel (i, j) */
const carved = (c, i, j, w, hgt, seed) => i === 0 || j === 0 || j === hgt - 1 ? hash(c, i + j * 7, seed) < 0.55 : hash(c, i + j * 7, seed + 1) < 0.28;
// INDIA in the attic band, in serifed capitals five rows high (I three pixels wide, N, D and A four), centred on the arch
const INSCRIPTION = (() => {
  const G = { I: ['111', '010', '010', '010', '111'], N: ['1001', '1101', '1011', '1001', '1001'], D: ['1110', '1001', '1001', '1001', '1110'],
    A: ['0110', '1001', '1111', '1001', '1001'] };
  return [0, 1, 2, 3, 4].map(j => [...'INDIA'].map(ch => G[ch][j]).join('0'));
})();
const INSCR_X = Math.round(IG.cx - INSCRIPTION[0].length / 2), INSCR_Y = 96;
const igLight = (u, h) => 0.52 + 0.2 * (1 - h / 42) + 0.07 * Math.exp(-(((u - 11.5) / 10) ** 2));
function igColour(z, u, h, x, y) {
  let L = igLight(u, h) + (vnoise(x / 1.6, y / 1.3, 62) - 0.5) * 0.07, ramp = TAN;
  const au = Math.abs(u - 11.5), row = Math.round(y);
  if (z === 'plat') { ramp = REDST; L = 0.5 + (frac(h / 0.47) > 0.72 ? 0.14 : frac(h / 0.47) < 0.25 ? -0.14 : 0); }
  else if (z === 'wall') {
    if (h < 3.2) { ramp = REDST; L = 0.46 + (h > 2.9 ? 0.2 : 0) + (frac(h / 0.9) < 0.12 ? -0.1 : 0) + (hash(x >> 1, row, 63) - 0.5) * 0.08; }
    else {
      if (frac(h / 0.95) < 0.11) L -= 0.06;                                               // ashlar courses
      for (const [a, b, k] of [[8.7, 9.3, 1], [15.1, 15.9, 1.3], [24.2, 25, 1.4]]) if (h >= a && h < b) L += (h < a + (b - a) * 0.35 ? 0.2 : h > b - (b - a) * 0.3 ? -0.14 : 0.04) * k;
      const ring = h > 15.5 ? Math.hypot(au, h - 15.5) - 4.55 : au - 4.55;
      if (ring >= 0 && ring < 0.55) L += 0.08;                                            // the dressed ring round the arch
      if (au < 0.6 && h >= 19.2 && h < 21.1) L += au > 0.35 ? -0.06 : 0.2;                // the keystone
      if (u < 0.35 || u > 22.65) L -= 0.07;
      // the dedication over the arch: three lines of small carved marks
      if (u > 1.8 && u < 21.2 && [114, 112, 110].includes(row) && hash(x, row, 64) < 0.62 && hash(x >> 2, row, 65) < 0.86) L -= 0.24;
    }
  } else if (z === 'attic') {
    L -= 0.03;
    if (h < 25.35) L += 0.12;
    // the band with the name and the two dates: INDIA cut in the middle, the dates in smaller carved marks either side
    const gx = Math.round(x) - INSCR_X, gy = row - INSCR_Y, wide = INSCRIPTION[0].length;
    if (gy >= 0 && gy < 5 && gx >= 0 && gx < wide && INSCRIPTION[gy][gx] === '1') L -= 0.24;
    for (const [x0, g0] of [[INSCR_X - 20, 20], [INSCR_X + wide + 3, 30]]) {
      const lx = x - x0, ly = row - 98;
      if (ly >= 0 && ly < 3 && lx >= 0 && lx < 17 && lx % 3 < 2 && carved(g0 + Math.floor(lx / 3), lx % 3, ly, 2, 3, 63)) L -= 0.18;
    }
  } else if (z === 'frieze') {
    const c = 1.25 + Math.round((u - 1.25) / 2.3) * 2.3, dr = Math.hypot(u - c, h - 31.2);
    L += dr < 0.38 ? 0.16 : dr < 0.88 ? (hash(Math.round(Math.atan2(h - 31.2, u - c) * 3), x, 66) < 0.5 ? -0.14 : -0.02) : -0.04;
    if (h < 30.3 || h > 32.1) L -= 0.08;
  } else if (z === 'cornice') {
    const q = h - 32.4;
    L += q < 0.35 ? 0.26 : q < 0.8 ? (x % 2 ? -0.2 : 0.1) : q < 1.6 ? 0.06 : -0.16;
  } else if (z === 'stageA') L += (h < 34.8 ? -0.22 : h > 36.4 ? 0.1 : -0.04) - (u < 3.6 || u > 19.4 ? 0.06 : 0);
  else if (z === 'stageB') L += (h < 37.1 ? -0.2 : h > 38.2 ? 0.08 : -0.08);
  else if (z === 'lip') L += 0.12;
  else if (z === 'dome') { const k = (h - 39.1) / 2.9; L = 0.5 + 0.16 * (1 - k) - 0.14 * (au / 5) ** 2 + (u < 10 && u > 8.5 ? 0.06 : 0); }
  return pick(ramp, L, x, y);
}
function renderIG() {
  const p = pix(W, H), s = IG.s;
  const bd = IG.d + IG.dd, bs = F / bd, bcx = PX(bd, AXIS), bsp = PY(bd, 15.5), br = 4.55 * bs, bfl = PY(bd, 1.4);
  const fx0 = IG.cx - 4.55 * s;                                                          // the front opening's left jamb
  for (let y = IG_TOP - 1; y < 178; y++) for (let x = Math.floor(IG.x0) - 4; x <= Math.ceil(IG.x1) + 1; x++) {
    const u = (x + 0.5 - IG.x0) / s, h = (IG.base - (y + 0.5)) / s, z = igFront(u, h);
    if (!z) continue;
    if (z !== 'arch') { p.set(x, y, igColour(z, u, h, x, y)); continue; }
    const X = x + 0.5, Y = y + 0.5;
    const back = Math.abs(X - bcx) < br && (Y > bsp ? Y < bfl : Math.hypot(X - bcx, Y - bsp) < br);
    if (back) continue;                                                                  // seen through: the lawns beyond
    if (Y > bsp + 1 && X < bcx) {                                                        // the passage's north wall, names carved in rows
      const f = clamp((X - fx0) / Math.max(1, bcx - br - fx0), 0, 1);
      p.set(x, y, pick(TAN, 0.62 - 0.26 * f - (y % 2 === 0 && hash(x, y, 67) < 0.4 ? 0.1 : 0), x, y));
    } else p.set(x, y, pick(TAN, 0.56 - ((x + y) % 3 === 0 || (x - y + 99) % 3 === 0 ? -0.04 : 0.14) + (Y > bsp - 2 ? -0.08 : 0), x, y));   // the coffered vault
  }
  // the south face, seen edge-on: the wall to the cornice with the smaller side arch, open high up
  const sideL = AXIS + IG.w / 2;
  for (let x = Math.floor(IG.x1); x < Math.ceil(PX(bd + 1, sideL - 0.6)) + 1; x++) {
    const X = x + 0.5;
    for (const [L, h0, h1, zone] of [[sideL, 1.4, 32.4, 'main'], [sideL + 0.6, 32.4, 34.4, 'cornice'], [sideL + 0.6, 0, 1.4, 'plat']]) {
      const d = F * L / (X - VPX);
      if (d < IG.d - 0.7 || d > bd + 0.7 || X >= VPX) continue;
      const v = d - IG.d;
      for (let y = Math.floor(PY(d, h1)); y <= Math.ceil(PY(d, h0)); y++) {
        const h = EYE - (y + 0.5 - HZ) * d / F;
        if (h < h0 || h >= h1 || p.alpha(x, y)) continue;
        let col;
        if (zone === 'plat') col = pick(REDST, 0.36, x, y);
        else if (zone === 'cornice') col = pick(TAN, 0.42 + (h < 32.8 ? 0.16 : 0), x, y);
        else if (v > 3.4 && v < 8.6 && ((h > 5.8 && (h < 9 || Math.hypot(v - 6, h - 9) < 2.6)) || (h < 4.2 && Math.abs(v - 6) < 0.9))) col = h < 4.2 ? '#2a1610' : pick(['#2e1812', '#4a2a1a', '#6a3e22'], 0.35 + (h - 5.8) / 10, x, y);
        else col = h < 3.2 ? pick(REDST, 0.34, x, y) : pick(TAN, 0.4 + 0.14 * (1 - h / 34) + (frac(h / 0.95) < 0.11 ? -0.06 : 0) + (h > 24.2 && h < 25 ? 0.1 : 0), x, y);
        p.set(x, y, col);
      }
    }
  }
  for (const [u0, h0, h1] of [[19.8, 34.4, 36.8], [17.7, 36.8, 38.6]]) {                          // the attic stages' south sides
    const xs = Math.round(IG.x0 + u0 * s);
    for (let x = xs; x < xs + 2; x++) for (let y = Math.round(igY(h1)); y < Math.round(igY(h0)); y++) if (!p.alpha(x, y)) p.set(x, y, pick(TAN, 0.4 - (x - xs) * 0.06, x, y));
  }
  // under the arch, the Amar Jawan Jyoti cenotaph (its flame joined the War Memorial's in 2022): black marble, a rifle and helmet
  const cd = IG.d + 6, ccx = PX(cd, AXIS), cs = F / cd;
  for (let y = Math.round(PY(cd, 3.2)); y < Math.round(PY(cd, 1.4)); y++) for (let x = Math.round(ccx - 1.7 * cs); x <= Math.round(ccx + 1.7 * cs); x++)
    p.set(x, y, y === Math.round(PY(cd, 3.2)) ? '#6a5448' : '#141018');
  const rx = Math.round(ccx), ry = Math.round(PY(cd, 3.2));
  p.fill(rx, ry - 3, 1, 3, '#2e2426'); p.fill(rx - 1, ry - 4, 3, 1, '#4a3c36');
  return trim(p);
}

/* ================= The ground, from the gate's lawns to our feet, projected with the camera ================= */
const GRASS = ['#040a0a', '#07100c', '#0b1810', '#112214', '#1a2e18', '#26401c', '#385420', '#4e6a28', '#6a7e34', '#8a9244'];
const RGRAN = ['#0e080c', '#1a0e10', '#281416', '#3a1c1a', '#4e2620', '#643226', '#7c402e', '#965238', '#b06848', '#c8845c'];
const STONE = ['#16100e', '#2a201a', '#443428', '#62503c', '#806a50', '#a08866', '#c0a680', '#dcc49c'];
const ASPH = ['#07070b', '#0c0c12', '#131319', '#1c1b21', '#26242a', '#322e32', '#423a3a', '#564a44'];
const roadLampL = l => (l.x - VPX) * ROAD_LAMP.d / F;
const GROUND_LAMPS = [
  ...NEAR_LAMPS.map(l => ({ d: l.d, L: l.L, r: 9, k: 0.55 })),
  ...FAR_LAMPS.map(l => ({ d: l.d, L: l.L, r: 8.5, k: 0.5 })),
  { d: KIOSK.d - 3, L: KIOSK.L, r: 7, k: 0.42 },                              // the chai kiosk's tube light
  { d: BHUTTA.d, L: BHUTTA.L, r: 3.4, k: 0.3 }, { d: KULFI.d, L: KULFI.L, r: 3.2, k: 0.28 },
  ...ROAD_LAMPS.map(l => ({ d: ROAD_LAMP.d, L: roadLampL(l), r: 17, k: 0.4 })),
];
/** the gate's floodlights on the lawns round it */
const igSpill = (d, L) => 0.55 * Math.max(0, 1 - Math.hypot((d - IG.d) / 110, (L - AXIS) / 34)) ** 1.5
  + 0.22 * Math.max(0, 1 - Math.hypot((d - CAN.d) / 70, (L - AXIS) / 14)) ** 2;
/** what the camera sees below the horizon at pixel (x, y): a material and the world point, or 'water' */
function groundHit(x, y) {
  const Y = y + 0.5 - HZ, X = x + 0.5 - VPX, dg = F * EYE / Y, Lg = X * dg / F;
  if (Lg >= CANAL.Ln || dg >= CANAL.end || Lg < CANAL.Lf) return { d: dg, L: Lg, m: null };
  const dw = F * (EYE + CANAL.dep) / Y, Lw = X * dw / F;
  if (dw <= CANAL.end && Lw >= CANAL.Lf) return { m: 'water' };
  if (dw > CANAL.end) return { d: CANAL.end, L: X * CANAL.end / F, m: 'endkerb' };
  return { d: F * CANAL.Lf / X, L: CANAL.Lf, m: 'kerb' };
}
/** the material at world point (d, L) on the lawn plane */
function material(d, L) {
  if (d > IG.d) return 'far';
  if (d > ROAD.d1) return 'hexlawn';
  if (d > ROAD.d0) return d > ROAD.d1 - 1.4 || d < ROAD.d0 + 1.2 ? 'roadkerb' : 'road';
  if (d > CANAL.end + 3) return 'footpath';
  if (L >= CANAL.Ln) return L < -2.5 ? 'coping' : L < 0 ? 'walk' : 'lawn';
  if (L >= CANAL.Lf) return 'lawn';                                            // the strip beyond the canal's end
  if (L >= -16.5) return 'coping';
  if (L >= -20) return 'walk';
  if (L >= -33.5) return 'lawn';
  if (L >= -35) return 'coping';
  if (L >= -65) return 'road';
  return 'lawn';
}
function groundColour(m, d, L, x, y) {
  const ppm = F / d;                                                           // pixels per metre there
  let v = 0.1 + worldLight(d, L, GROUND_LAMPS) + (d > 80 ? igSpill(d, L) : 0);
  switch (m) {
    case 'hexlawn': case 'lawn': {
      v += 0.08 + (vnoise(d * 0.9, L * 0.9, 71) - 0.5) * 0.12;
      if (ppm > 14 && hash(x, y, 72) < 0.12) v += 0.1;                        // blades catching the light
      if (m === 'hexlawn' && Math.abs(L - AXIS) < 3.5 && d < IG.d - 4) v += 0.06;   // the path up to the gate
      return pick(GRASS, v, x, y);
    }
    case 'walk': {
      const a = Math.floor(d / 1.1), b = Math.floor(L / 0.84), jw = 0.05 + 0.7 / ppm;
      if (ppm > 5 && (frac(d / 1.1) < jw || frac(L / 0.84) < jw * 1.3)) return pick(RGRAN, v * 0.7 + 0.02, x, y);
      return pickFlat(RGRAN, v + 0.1 + (hash(a, b, 73) - 0.5) * 0.12, x, y);
    }
    case 'coping': case 'endkerb': case 'kerb': {
      const face = m !== 'coping', a = Math.floor(d / 1.2);
      if (ppm > 6 && frac(d / 1.2) < 0.05 + 0.6 / ppm) return pick(STONE, v * 0.6, x, y);
      return pick(face ? REDST : STONE, v * (face ? 0.9 : 1) + (face ? 0.02 : 0.08) + (hash(a, Math.round(L), 74) - 0.5) * 0.08, x, y);
    }
    case 'footpath': return pick(RGRAN, v + 0.04 + (hash(x >> 1, y, 75) - 0.5) * 0.06, x, y);
    case 'roadkerb': {                                                          // Delhi's black-and-yellow kerbs
      const band = Math.floor(L / 1.2) & 1;
      return band ? pick(['#1a160c', '#4a3e18', '#8a7428', '#c8a838'], v + 0.2, x, y) : pick(ASPH, v * 0.6, x, y);
    }
    case 'road': {
      v += (hash(x, y, 76) - 0.5) * 0.06;
      if (d > ROAD.d0 && Math.abs(d - 110) < 0.8 && frac(L / 9) < 0.34) return pick(['#3a3a3e', '#6a6a6a', '#9a9a92'], v + 0.3, x, y);   // lane dashes
      return pick(ASPH, v + 0.06, x, y);
    }
  }
  return pick(GRASS, v, x, y);
}
/** the ground as two canvases: beyond the canal's end (the hexagon's lawns and road) and our side (water left open) */
function renderGround() {
  const far = pix(W, H), near = pix(W, H), water = [];
  for (let y = 177; y < H; y++) {
    let xa = -1, xb = -1;
    for (let x = 0; x < W; x++) {
      const g = groundHit(x, y);
      if (g.m === 'water') { if (xa < 0) xa = x; xb = x + 1; continue; }
      const m = g.m || material(g.d, g.L);
      if (m === 'far') continue;
      (y < 185 ? far : near).set(x, y, groundColour(m, g.d, g.L, x, y));
    }
    if (xa >= 0) water[y] = [xa, xb];
  }
  // saptaparni florets fallen on our lawn and promenade, thickest under the trees
  const fr = rng(88);
  for (let k = 0; k < 170; k++) {
    const d = 17 + fr() * 50, L = -2.4 + fr() * 7.5, x = Math.round(PX(d, L)), y = Math.round(PY(d));
    if (x < 0 || x >= W || y >= H || groundHit(x, y).m === 'water' || fr() > 0.35 + 0.65 * smooth(1, 4, L)) continue;
    near.set(x, y, fr() < 0.5 ? '#d8d4b4' : '#a8a888');
    if (d < 30 && fr() < 0.5) near.set(x + 1, y, '#bcb898');
  }
  // the chain between the stone bollards along the avenue's edge (far left)
  for (let d = 58; d < 92; d += 3) {
    const x = PX(d, -34.2), b = PY(d), s = F / d;
    if (x < -4 || x > W) continue;
    near.fill(Math.round(x), Math.round(b - 0.7 * s), Math.max(1, Math.round(0.25 * s)), Math.round(0.7 * s), '#6a5a4c');
    near.set(Math.round(x), Math.round(b - 0.7 * s), '#a08a70');
    const x2 = PX(d + 3, -34.2), b2 = PY(d + 3), s2 = F / (d + 3);
    for (let k = 1; k < 8; k++) { const u = k / 8; near.set(lerp(x, x2, u), lerp(b - 0.55 * s, b2 - 0.55 * s2, u) + Math.sin(Math.PI * u) * 1.2, '#1a1414'); }
  }
  return { far: trim(far), near: near.done(), water };
}

/* ================= The C-Hexagon: tall road lamps, a police van, the crowd round the gate ================= */
function renderRoadLamps() {
  const p = pix(W, H), s = F / ROAD_LAMP.d, b = Math.round(PY(ROAD_LAMP.d)), top = Math.round(PY(ROAD_LAMP.d, ROAD_LAMP.hgt));
  for (const l of ROAD_LAMPS) {
    for (let y = top; y < b; y++) { p.set(l.x, y, '#16141a'); if (y > b - 5) p.set(l.x + 1, y, '#1c1a20'); }
    for (const dir of [-1, 1]) { for (let k = 1; k <= 5; k++) p.set(l.x + dir * k, top - (k < 3 ? 1 : 0), '#1c1a20'); p.fill(dir < 0 ? l.x - 7 : l.x + 5, top, 3, 1, '#2a2830'); }
  }
  return trim(p);
}
const ROAD_HEADS = ROAD_LAMPS.flatMap(l => [l.x - 6, l.x + 6]);
// the crowd strolling on the lawns round the gate, tiny against the floodlit grass
const STROLLERS = [];
{
  const r = rng(81);
  for (let k = 0; k < 22; k++) {
    const d = 132 + r() * 110, x = 150 + r() * 250;
    if (Math.abs(x - CAN.cx) < 9 && d > 200) continue;
    STROLLERS.push({ d, x, a: 3 + r() * 14, k: 1 + Math.floor(r() * 3), ph: r(), sit: r() < 0.3,
      col: ['#e8e0d0', '#c83a4a', '#3a5a9a', '#e0b040', '#2a2a30', '#8a3a8a', '#4a8a5a'][Math.floor(r() * 7)] });
  }
  STROLLERS.sort((a, b) => b.d - a.d);
}
function drawStrollers(t) {
  for (const q of STROLLERS) {
    const s = F / q.d, hgt = Math.round((q.sit ? 0.9 : 1.65) * s), fy = Math.round(PY(q.d));
    const x = Math.round(q.x + (q.sit ? 0 : q.a * Math.sin(TAU * (q.k * t / LOOP + q.ph))));
    ctx.fillStyle = '#120c12'; ctx.fillRect(x, fy - hgt, 2, hgt);                    // lit from behind by the gate
    ctx.fillStyle = q.col; ctx.fillRect(x, fy - hgt + 2, 1, Math.max(1, hgt - 4));
    if (q.sit) { ctx.fillStyle = '#120c12'; ctx.fillRect(x + 2, fy - 1, 2, 1); }
  }
}
// a police van parked on our side of the hexagon where the avenue's carriageway meets it, behind the avenue's chain
// bollards, nose to the left: the hexagon's traffic passes behind it
const PCR = { d: 96, x: 58 };
PCR.base = Math.round(PY(PCR.d));
function drawPCR(t) {
  blit(PCR_C);
  const ph = frac(t * 1.25 + 0.1), red = ph < 0.25 || (ph > 0.5 && ph < 0.62), blue = (ph > 0.25 && ph < 0.4) || ph > 0.75, y = PCR.base - 15;
  rect(ctx, PCR.x + 12, y, 2, 1, red ? '#ff3a3a' : '#5a1a1a'); rect(ctx, PCR.x + 15, y, 2, 1, blue ? '#4a7aff' : '#1a2a5a');
  if (red) halo(HL.beaconR, PCR.x + 12, y, 0.9);
  if (blue) halo(HL.beaconB, PCR.x + 16, y, 0.9);
}
function renderPCR() {                                  // 4 m long at 7.6 px/m: a boxy van, white with the blue and red bands
  const p = pix(30, 16);
  for (let y = 2; y < 13; y++) for (let x = 0; x < 30; x++) {
    const front = 5 - (y - 2);                                                          // the raked windscreen
    if (y < 6 && (x < front || x > 28 - (y === 2 ? 1 : 0))) continue;
    let col = y === 2 ? '#c8ccd4' : '#e4e6ee';
    if (y >= 3 && y < 6 && x <= front + 1) col = x === front ? '#c8ccd4' : '#2a3448';   // the windscreen
    else if (y >= 3 && y < 6 && x > front + 2 && x < 27 && x !== 11 && x !== 19) col = '#2a3448';   // side windows
    if (x === 29 && y > 2 && y < 12) col = '#b8bcc6';                                   // the rear, in shade
    if (y === 8) col = '#3a5ac8'; if (y === 9) col = '#c83a3a';                         // the livery bands
    if (y === 12) col = '#8a8c96';
    p.set(x, y, col);
  }
  p.set(0, 7, '#ffe6a8'); p.set(29, 7, '#c83a2a');                                       // parking lights
  p.fill(11, 1, 8, 1, '#3a3a44');                                                        // the light bar's base
  for (const wx of [6, 23]) { p.fill(wx - 2, 12, 5, 4, '#08080a'); p.fill(wx - 1, 13, 2, 2, '#6a6a72'); }
  return { c: p.done(), x: PCR.x, y: PCR.base - 15 };
}

/* ================= Traffic round the hexagon, one way (right to left from here) ================= */
const VPAL = {
  auto: { y: '#f0c828', Y: '#b08a18', w: '#4a5a6a', g: '#2a8a3a', G: '#1a5a26', i: '#140e10', h: '#1a1210', f: '#b07a58', s: '#e8e0d0' },
  car: [['#e8e8ec', '#a8a8b4'], ['#b8bcc4', '#7a7e88'], ['#8a1a1e', '#5a1014'], ['#1a1c24', '#0e0e14'], ['#3a4a6a', '#24304a'], ['#c8b080', '#8a7650']],
};
const AUTO_ROWS = [
  '....yyyyyyyyyyyy..', '...yyyyyyyyyyyyyy.', '..YY..........YYy.', '..Y.iihiiiihhiiiy.', '.w..iifiiiiffiiiG.', '.w.iissiiissssiiG.',
  '.w.iissiiissssiiG.', 'Lggggggggggggggggt', 'gggggggggggggggggG', '.GkkkGGGGGGGGGkkkG', '..kok.........kok.', '...k...........k..'];
const CAR_ROWS = [
  '........bbbbbbbbb.........', '......bbwwwwbwwwwbb.......', '.....bwwwwwwbwwwwwwb......', '..bbbbbbbbbbbbbbbbbbbbbb..',
  'Lbbbbbbbbbbbbbbbbbbbbbbbbt', 'bbbbbbbbbbbbbbbbbbbbbbbbbb', 'DbbbbbbbbbbbbbbbbbbbbbbbbD', '.DDkkkDDDDDDDDDDDDDDkkkDD.',
  '...kok..............kok...', '....k................k....'];
const AMB_ROWS = [
  '..........aaaaaaaaa.........', '........aawwwwawwwwaa.......', '.......awwwwwwawwwwwwa......', '....aaaaaaaaaaaaaaaaaaaaa...',
  '..aaaaaaaaaaaaaaaaaaaaaaaaa.', 'Laaaaaaaaaaaaaaaaaaaaaaaaaat', 'caaaaaaaaaaaaaaaaaaaaaaaaaac', '.AaaaaaaaaaaaaaaaaaaaaaaaaA.',
  '..AAkkkAAAAAAAAAAAAAAkkkAA..', '....kok..............kok....', '.....k................k.....'];
const BIKE_ROWS = [
  '.....hh.hh....', '.....ff.ff....', '....sss.sss...', '....sss.sss...', '...ssssssss...', '..ssssssss....',
  'L..bbbbbbb.t..', '.bbbbbbbbbbb..', 'kkk......kkk..', 'kok......kok..', 'kkk......kkk..'];
function renderBus(kind) {
  const w = 80, h = 21, p = pix(w, h), body = kind === 'red' ? ['#b82a2a', '#7a1a1e'] : ['#2a8a4a', '#1a5a30'];
  for (let y = 1; y < 18; y++) for (let x = 0; x < w; x++) {
    if (y < 3 && x < 2) continue;
    let col = y < 3 ? '#d8d8dc' : y < 11 ? '#d8d8dc' : y === 11 ? '#f0f0f0' : body[0];
    if (y >= 3 && y < 10 && x >= 2 && x < 78) {                                           // the windows, lit inside
      const bay = Math.floor((x - 2) / 8), cx = (x - 2) % 8;
      col = cx === 0 ? '#2a2a30' : hash(bay, y >> 2, kind === 'red' ? 91 : 92) < 0.15 ? '#c8b890' : y < 5 ? '#fff4d8' : '#ffe6a8';
      if (y >= 5 && y < 8 && cx > 1 && cx < 6 && hash(bay, cx, 93) < 0.45) col = y === 5 ? '#2a1a14' : '#3a2a2a';   // passengers
      if (x < 5) col = y < 5 ? '#ff9a2a' : '#1a1a24';                                     // the destination board (glyphs) and screen
    }
    if (y >= 11 && ((x >= 8 && x < 14) || (x >= 42 && x < 48))) col = y < 16 ? '#ffe6a8' : '#8a8a92';   // the doors
    if (y >= 15 && y < 18) col = y === 17 ? '#2a2a30' : body[1];
    p.set(x, y, col);
  }
  if (kind === 'red') p.fill(24, 0, 26, 1, '#b8b8c0');                                   // the AC unit on the roof
  for (let x = 1; x < 4; x += 2) p.set(x, 3, '#ffd24a');
  for (const wx of [14, 64]) { for (let y = 16; y < 21; y++) for (let x = wx - 4; x <= wx + 4; x++) if (Math.hypot(x - wx, y - 18) < 3.5) p.set(x, y, Math.hypot(x - wx, y - 18) < 1.5 ? '#6a6a72' : '#08080a'); }
  p.set(0, 13, '#fff4d0'); p.set(0, 14, '#fff4d0'); p.set(w - 1, 13, '#ff3a2a'); p.set(w - 1, 14, '#ff3a2a');
  return p.done();
}
const VTYPES = { auto: 18, car: 26, amb: 28, bike: 14, bus: 80 };
const TRAFFIC = [];
{
  const r = rng(95), SPEED = [46, 58];
  const BUS_AT = [[70, 190], [30, 140]], AMB_AT = [[110], [205]];                // when each lane's buses and Ambassador come by
  for (const lane of [0, 1]) {
    let t = lane * 3.3, nb = 0, na = 0;
    while (t < LOOP - 4) {
      const q = r();
      let type = q < 0.42 ? 'auto' : q < 0.8 ? 'car' : 'bike';
      if (nb < BUS_AT[lane].length && t >= BUS_AT[lane][nb]) { type = 'bus'; nb++; }
      else if (na < AMB_AT[lane].length && t >= AMB_AT[lane][na]) { type = 'amb'; na++; }
      TRAFFIC.push({ t0: +t.toFixed(2), lane, type, v: SPEED[lane], col: Math.floor(r() * 6), bus: (lane + nb) % 2 ? 'red' : 'green' });
      t += (VTYPES[type] + 18) / SPEED[lane] + 1.5 + r() * 6;
    }
  }
}
/** the x of vehicle v at t (right to left, wrapping with the loop), or null when off screen */
function vehicleX(v, t) {
  const s = ((t - v.t0) % LOOP + LOOP) % LOOP, x = W + 8 - v.v * s;
  return x < -VTYPES[v.type] - 8 ? null : Math.round(x);
}
function drawTraffic(t, lane) {
  const base = LANES[lane].base;
  for (let i = 0; i < TRAFFIC.length; i++) {
    const v = TRAFFIC[i];
    if (v.lane !== lane) continue;
    const x = vehicleX(v, t);
    if (x === null) continue;
    const spr = VEH[i], y = base - spr.height + 1;
    ctx.drawImage(spr, x, y);
    const hy = y + (v.type === 'bus' ? 13 : v.type === 'auto' ? 7 : v.type === 'bike' ? 6 : v.type === 'amb' ? 5 : 4);
    halo(HL.head, x - 2, hy, 0.85); halo(HL.headCore, x, hy, 0.8);
    halo(HL.tail, x + spr.width - 1, hy, 0.7);
  }
}
/* the ice-cream man's tricycle on the hexagon's footpath, left to right, ringing his bell by the gate */
const TRIKE = [{ t0: 34, v: 10 }, { t0: 146, v: 10 }];
const trikeX = (k, t) => -20 + k.v * (t - k.t0);
function drawTrike(t) {
  for (const k of TRIKE) {
    const x = Math.round(trikeX(k, t));
    if (x < -20 || x > W + 4) continue;
    const y = TRIKE_Y, pd = Math.floor(t * 3) & 1;
    ctx.drawImage(TRIKE_C, x, y);
    rect(ctx, x + 3 - pd, y + 11, 1, 1, '#2a2020'); rect(ctx, x + 2 + pd, y + 9, 1, 1, '#3a2a2a');   // pedalling feet
    const bell = TRIKE_BELL.some(b => t >= b && t < b + 1.6) && (Math.floor(t * 12) & 1);
    rect(ctx, x + 5, y + 3, 1, 1, bell ? '#fff6c0' : '#b8a060');
    halo(HL.trike, x + 10, y + 5, 0.6);
  }
}
const TRIKE_BELL = TRIKE.map(k => +(k.t0 + (IG.cx - 12 + 20) / k.v).toFixed(1));
const TRIKE_Y = Math.round(PY(94)) - 12;
function renderTrike() {                                // drawn facing left, then mirrored: the ice box rides in front
  return spritePair([
    '...........hh...', '...........ff...', '..bbbbbbb..ss...', '.bwwwwwwwb.sss..', '.bwpwywpwb.sss..', '.bwwwwwwwb..s...',
    '.bbbbbbbbbkkkk..', '..k.....k...k...', '.kok...kok..k...', '.kkk...kkk.kok..', '..k.....k..kkk..', '...........k....', '................'],
  { b: '#3a6ac8', w: '#f0f0f4', p: '#ff7ab0', y: '#ffd24a', k: '#141014', o: '#8a8a92', h: '#1a1210', f: '#b07a58', s: '#e8d8b8' }).left;
}

/* ================= The canal: the gate's reflection, the traffic's lights, moored pedal boats ================= */
// Every object mirrors about its own line: the row its foot would have if it stood on the water, PY(d, -dep)
const mirrorRow = d => PY(d, -CANAL.dep);
const WATER = ['#04060e', '#070b16', '#0a101e', '#0e1626', '#141c2e', '#1c2236', '#282a3c', '#38323e'];
function renderWater(spans) {
  const p = pix(W, H), m = pix(W, H);
  for (let y = WTOP; y < H; y++) {
    const sp = spans[y];
    if (!sp) continue;
    for (let x = sp[0]; x < sp[1]; x++) {
      const far = 1 - (y - WTOP) / (H - WTOP);
      let v = 0.18 + far * 0.3 + Math.max(0, 1 - Math.hypot((x - IG.cx) / 60, (y - 190) / 30)) * 0.2;
      v += (vnoise(x / 9, y / 1.2, 101) - 0.5) * 0.12;
      p.set(x, y, pick(WATER, v, x, y));
      m.set(x, y, '#ffffff');
    }
  }
  return { c: p.done(), mask: m.done() };
}
/** mirror a layer canvas (drawn at 0,0) about row M into the mirror context */
function mirrorInto(mx, layer, M, lx = 0, ly = 0) {
  mx.setTransform(1, 0, 0, -1, 0, 2 * M);
  mx.drawImage(layer, lx, ly);
  mx.setTransform(1, 0, 0, 1, 0, 0);
}
const ripple = (y, t) => Math.round(Math.sin(TAU * 45 * t / LOOP + y * 0.55) * (0.3 + (y - WTOP) * 0.014)
  + Math.sin(TAU * 110 * t / LOOP - y * 1.3) * 0.55);
// a clipped hedge on the strip of lawn between the canal's end and the hexagon's footpath, from the gate's south face to the
// promenade: the boats read against it, and the traffic behind it shows only its roofs, never through the boats
const HEDGE = { d: 92.2, L0: -12.9, L1: -2.4, hgt: 1.05 };
const HEDGE_LEAF = ['#040a08', '#08130c', '#0e1c12', '#152818', '#1e341c', '#2c4422', '#42562a', '#5e6a32'];
function renderHedge() {
  const p = pix(W, H), x0 = Math.round(PX(HEDGE.d, HEDGE.L0)), x1 = Math.round(PX(HEDGE.d, HEDGE.L1)), b = Math.round(PY(HEDGE.d));
  for (let x = x0; x < x1; x++) {
    const top = Math.round(PY(HEDGE.d, HEDGE.hgt) + (vnoise(x / 4, 0, 970) - 0.5) * 1.4) + (x === x0 || x === x1 - 1 ? 1 : 0);
    for (let y = top; y < b; y++) {
      const k = (y - top) / Math.max(1, b - top);
      let v = 0.36 - k * 0.22 + (vnoise(x / 1.6, y / 1.2, 971) - 0.5) * 0.28 + 0.12 * smooth(330, 284, x);   // the gate's glow on its left end
      if (y === top) v += 0.4; else if (y === top + 1) v += 0.16;                                            // the clipped top, lit
      p.set(x, y, pick(HEDGE_LEAF, v, x, y));
    }
  }
  return trim(p);
}
// pedal boats moored at the canal's end, broadside to us, bobbing: two, with water between them, in the stretch of the end
// wall between the gate's floodlight (x 277) and the nearest lamp post (x 329)
const BOATS = [{ x: 281, w: 23, swan: true, hull: '#e04a8a', dk: '#8a1a4a', k: 23 }, { x: 309, w: 19, hull: '#e8b030', dk: '#8a6418', k: 29 }];
const BOAT_D = 88.5;
function renderBoat(b) {
  const p = pix(b.w, 13);
  if (b.swan) {                                                                   // a swan pedal boat, neck up at the bow
    for (let x = 3; x < 21; x++) for (let y = 6; y < 12; y++) {
      const dx = (x - 12) / 9, dy = (y - 9) / 3;
      if (dx * dx + dy * dy > 1.1) continue;
      p.set(x, y, y === 6 ? '#ffffff' : y > 10 ? '#8a8a9a' : x > 15 ? '#d8d8e2' : '#f2f2f6');
    }
    for (const [x, y] of [[20, 5], [21, 4], [21, 5], [20, 6]]) p.set(x, y, '#e8e8ee');           // the tail
    for (const [x, y] of [[3, 6], [2, 5], [2, 4], [2, 3], [3, 2], [3, 1], [4, 0], [5, 0], [5, 1]]) p.set(x, y, '#f6f6fa');
    p.set(6, 1, '#ff8a2a'); p.set(7, 1, '#e86a1a'); p.set(4, 1, '#1a1a1a');       // beak and eye
    for (let x = 9; x < 17; x++) p.set(x, 8 + (x > 13 ? 1 : 0), '#b8b8c8');      // a wing
    p.fill(9, 4, 3, 2, b.hull); p.fill(14, 4, 3, 2, b.hull);                      // the seats
    return p.done();
  }
  const w = b.w;
  for (let x = 1; x < w - 1; x++) for (let y = 7; y < 12; y++) {                // a plain pedal boat, prow up at the bow
    if (y > 9 && (x < 3 || x > w - 4)) continue;
    if (y === 11 && (x < 4 || x > w - 5)) continue;
    p.set(x, y, y === 7 ? '#f0e8dc' : y === 11 ? b.dk : x < 6 ? mixHex(b.hull, '#ffffff', 0.18) : b.hull);
  }
  p.set(0, 6, '#f0e8dc'); p.set(1, 6, b.hull); p.set(0, 5, b.hull);
  for (const x0 of [5, 9]) { p.fill(x0, 4, 3, 3, b.dk); p.fill(x0, 4, 3, 1, mixHex(b.hull, '#ffffff', 0.3)); }   // two seat backs
  p.fill(w - 6, 4, 4, 3, b.dk); p.fill(w - 6, 4, 4, 1, b.hull);                   // the paddle wheel's housing
  p.set(w - 4, 9, '#1a1a1a');
  return p.done();
}
const boatY = (b, t) => Math.round(PY(BOAT_D, -CANAL.dep)) - 11 + (wave(t, b.k, b.x * 0.01) > 0.35 ? 1 : 0);
function drawBoats(t) {
  for (let i = 0; i < BOATS.length; i++) ctx.drawImage(BOAT_C[i], BOATS[i].x, boatY(BOATS[i], t));
  ctx.fillStyle = '#6a5a4a';                                                      // their ropes to the end wall's rings
  for (const b of BOATS) { ctx.fillRect(b.x + b.w - 2, boatY(b, t) + 8, 2, 1); }
}
/** the reflections on the water: the static far scene mirrored at build time, then what moves, rippled row by row */
function drawWater(t, billboard) {
  ctx.drawImage(WATER_C.c, 0, 0);
  const r = REF_X;
  r.globalCompositeOperation = 'copy';
  r.drawImage(MIRROR_C, 0, 0);
  r.globalCompositeOperation = 'source-over';
  // the traffic and the ice-cream tricycle, each about its own line
  for (let i = 0; i < TRAFFIC.length; i++) {
    const v = TRAFFIC[i], x = vehicleX(v, t);
    if (x === null || x > 372) continue;
    const spr = VEH[i], M = mirrorRow(LANES[v.lane].d), y = LANES[v.lane].base - spr.height + 1;
    r.setTransform(1, 0, 0, -1, 0, 2 * M); r.drawImage(spr, x, y); r.setTransform(1, 0, 0, 1, 0, 0);
  }
  for (const k of TRIKE) {
    const x = Math.round(trikeX(k, t));
    if (x > -20 && x < W) { const M = mirrorRow(94); r.setTransform(1, 0, 0, -1, 0, 2 * M); r.drawImage(TRIKE_C, x, TRIKE_Y); r.setTransform(1, 0, 0, 1, 0, 0); }
  }
  mirrorInto(r, HEDGE_C.c, mirrorRow(HEDGE.d), HEDGE_C.x, HEDGE_C.y);                // the hedge hides the traffic's reflection as it hides the traffic
  for (let i = 0; i < BOATS.length; i++) { const M = mirrorRow(BOAT_D); r.setTransform(1, 0, 0, -1, 0, 2 * M); r.drawImage(BOAT_C[i], BOATS[i].x, boatY(BOATS[i], t)); r.setTransform(1, 0, 0, 1, 0, 0); }
  for (const c of COPTERS) {                                                     // the far toys' lights
    if (!c.far) continue;
    const q = copterAt(c, t);
    if (!q) continue;
    const y = Math.round(2 * mirrorRow(180) - q.y);
    if (y >= WTOP && y < H) { r.fillStyle = COP_COL[c.col][0]; r.fillRect(Math.round(q.x), y, 1, 2); }
  }
  if (billboard) { r.globalAlpha = 0.34; r.drawImage(BB_GLOW_C, BB.x - 4, Math.round(2 * mirrorRow(KIOSK.d) - BB.y - BB.h - 4)); r.globalAlpha = 1; }
  r.globalCompositeOperation = 'destination-in';
  r.drawImage(WATER_C.mask, 0, 0);
  r.globalCompositeOperation = 'source-over';
  for (let y = WTOP; y < H; y++) {
    ctx.globalAlpha = 0.76 - 0.4 * (y - WTOP) / (H - WTOP);
    ctx.drawImage(REF_C, 0, y, W, 1, ripple(y, t), y, W, 1);
  }
  ctx.globalAlpha = 1;
  // shimmering light: the gate's glow in a broken column, the kiosk's tube light, the lamp posts' reach on the surface
  streak(ctx, t, IG.cx - 2, 7, '#ffd890', 0.3, 1.3, 190, 236);
  streak(ctx, t, KIOSK.cx, 5, '#e8fff0', 0.3, 4.1, 197, 226);
  if (billboard) streak(ctx, t, BB.x + BB.w / 2, 12, '#ffb070', 0.3, 2.2, 224, 256);
  for (const l of FAR_LAMPS) drawPool(POOL.lampW, PX(l.d, l.L) + 2, PY(l.d, -CANAL.dep) + 4, 0.8);
  // glints of the sky and the lamps on the ripples
  for (let k = 0; k < 46; k++) {
    const y = WTOP + 1 + Math.floor(hash(k, 1, 102) * (H - WTOP - 1)), sp = WATER_SPANS[y];
    if (!sp) continue;
    const s = step(t + k * 0.37, 480 + k * 6), on = hash(k, s, 103);
    if (on < 0.45) continue;
    const x = Math.round(lerp(sp[0], sp[1] - 3, hash(k, s, 104))), len = 1 + Math.floor(hash(k, s, 105) * (y > 220 ? 4 : 2));
    ctx.globalAlpha = 0.25 + 0.35 * on; rect(ctx, x, y, len, 1, Math.abs(x - IG.cx) < 40 ? '#ffd8a0' : '#8a9ac8');
  }
  ctx.globalAlpha = 1;
}

/* ================= Lamp posts: black cast iron, a fluted base, a square lantern with a crown ================= */
/** paint a lamp post standing at (d, L) into p; returns its glass { x, y, w, h } for the per-frame light */
function paintLamp(p, d, L, hgt) {
  const s = F / d, x = Math.round(PX(d, L)), b = Math.round(PY(d)), top = PY(d, hgt);
  const pw = Math.max(1, Math.round(0.14 * s)), bw = Math.max(2, Math.round(0.38 * s)), bh = Math.round(0.9 * s);
  const gw = Math.max(3, Math.round(0.4 * s)) | 1, gh = Math.max(3, Math.round(0.58 * s)), gy = Math.round(top + 0.3 * s);
  const IRON = ['#0a080c', '#141018', '#201a22', '#3a2e30', '#5a4638'];
  const col = (i, n, lit) => IRON[clamp(Math.round((1 - i / Math.max(1, n - 1)) * 2 + lit), 0, 4)];
  for (let y = b - bh; y < b; y++) for (let i = 0; i < bw; i++) {                              // the fluted base, lit from the lantern
    const flute = bw > 4 && i % 2 === 1;
    p.set(x - (bw >> 1) + i, y, y < b - bh + 2 ? IRON[3] : flute ? IRON[0] : col(i, bw, 1));
  }
  for (let y = Math.round(gy + gh + 0.2 * s); y < b - bh; y++) for (let i = 0; i < pw; i++) p.set(x - (pw >> 1) + i, y, i === 0 && pw > 1 ? IRON[3] : IRON[1]);
  const collar = Math.round(lerp(b - bh, gy + gh, 0.35));
  p.fill(x - (pw >> 1) - 1, collar, pw + 2, Math.max(1, Math.round(0.08 * s)), IRON[2]);
  const gx = x - (gw >> 1);
  p.fill(gx - 1, gy + gh, gw + 2, Math.max(1, Math.round(0.07 * s)), IRON[2]);                   // the lantern's floor
  for (let y = gy + gh + 1; y < Math.round(gy + gh + 0.2 * s); y++) p.set(x, y, IRON[1]);
  for (let j = 0; j < gh; j++) { p.set(gx - 1, gy + j, IRON[1]); p.set(gx + gw, gy + j, IRON[1]); }
  const capH = Math.max(2, Math.round(0.26 * s));
  for (let j = 0; j < capH; j++) { const hw = Math.round(lerp(0.5, (gw + 3) / 2, (j + 1) / capH)); p.fill(x - hw, gy - capH + j, hw * 2 + 1, 1, j === capH - 1 ? IRON[3] : IRON[1]); }
  p.fill(x, gy - capH - Math.max(1, Math.round(0.14 * s)), 1, Math.max(1, Math.round(0.14 * s)), IRON[1]);   // the finial
  return { x: gx, y: gy, w: gw, h: gh, cx: x, cy: gy + gh / 2, s };
}
/** the lantern's glass, lit (frame bars stay dark) */
function drawGlass(g, lv) {
  rect(ctx, g.x, g.y, g.w, g.h, lv > 0.6 ? '#ffe6a8' : '#8a6a40');
  if (lv > 0.6) rect(ctx, g.x + 1, g.y + 1, Math.max(1, g.w - 2), Math.max(1, g.h - 2), '#fff6dc');
  if (g.w >= 5) rect(ctx, g.x + (g.w >> 1), g.y, 1, g.h, '#3a2e24');
  if (g.h >= 8) rect(ctx, g.x, g.y + (g.h >> 1), g.w, 1, '#6a4e30');
}

/* ================= Jamun trees along the avenue: dense dark crowns, lit from below by the lamps ================= */
const JAMUN = ['#030806', '#06100a', '#0a170e', '#0f2014', '#162a1a', '#203822', '#2e482a'];
function renderJamun(q) {
  const p = pix(W, H), r = rng(500 + q.seed), s = F / q.d, cx = PX(q.d, q.L), base = PY(q.d);
  const cy = PY(q.d, 7.2), rx = q.r * s, ry = q.r * 0.82 * s, tufts = [];
  for (let k = 0; k < 46; k++) {
    const a = r() * TAU, rr = Math.sqrt(r()) * 0.82;
    tufts.push({ x: cx + Math.cos(a) * rr * rx, y: cy + Math.sin(a) * rr * ry, rx: (0.22 + r() * 0.2) * rx, ry: (0.2 + r() * 0.16) * ry });
  }
  const best = (x, y) => { let v = 0, bt = null; for (const t of tufts) { const w = 1 - ((x - t.x) / t.rx) ** 2 - ((y - t.y) / t.ry) ** 2; if (w > v) { v = w; bt = t; } } return bt; };
  const tw = Math.max(2, Math.round(0.45 * s)), fork = PY(q.d, 3.6);
  for (let y = Math.round(fork); y < Math.round(base); y++) for (let i = 0; i < tw; i++)       // the trunk, lit on the lamp's side
    p.set(Math.round(cx + (base - y) * 0.03) - (tw >> 1) + i, y, i >= tw - 2 ? '#3a2a22' : i === 0 ? '#0e0a0c' : '#1e1616');
  for (const [dx, len] of [[-0.8, 0.9], [0.7, 1], [0.1, 0.8]]) for (let k = 0; k < len * 2.4 * s; k++) p.set(cx + dx * k * 0.7, fork - k * 0.8, '#161012');
  const lampSide = FAR_LAMPS.map(l => PX(l.d, l.L));
  for (let y = Math.floor(cy - ry - 4); y <= Math.ceil(cy + ry + 4); y++) for (let x = Math.floor(cx - rx - 6); x <= Math.ceil(cx + rx + 6); x++) {
    const t = best(x, y);
    if (!t || (hash(x, y, 510 + q.seed) < 0.06 && (!best(x - 2, y) || !best(x + 2, y) || !best(x, y - 2)))) continue;
    const dy = (y - t.y) / t.ry;
    let lv = 0.36 - dy * 0.18 + (vnoise(x / 1.8, y / 1.5, 520 + q.seed) - 0.5) * 0.4;
    if (!best(x, y - 1)) lv = 0.9;                                                               // the sky's cool rim on each clump
    p.set(x, y, pick(JAMUN, lv, x, y));
    if (!best(x, y + 1) || !best(x, y + 2)) {                                                    // undersides warmed by the lamps
      const near = Math.max(...lampSide.map(lx => clamp(1 - Math.abs(x - lx) / 70, 0, 1))) * smooth(cy - ry * 0.2, cy + ry, y);
      if (hash(x, y, 530) < 0.15 + near * 0.5) p.set(x, y, near > 0.55 ? '#4e4420' : near > 0.25 ? '#2e2e18' : '#162014');
    }
  }
  // jamun fruit is long gone by October; a few pale new leaves
  for (let k = 0; k < 12; k++) { const t = tufts[k]; p.set(t.x, t.y - t.ry * 0.6, '#3e5a30'); }
  return trim(p);
}

/* ================= The chai kiosk at the canal's head, and its lit sign on posts (the billboard) ================= */
function renderKiosk() {
  const p = pix(W, H), s = KIOSK.s, cx = KIOSK.cx, x0 = Math.round(cx - KIOSK.hw * s), x1 = Math.round(cx + KIOSK.hw * s);
  const b = KIOSK.base, top = Math.round(PY(KIOSK.d, KIOSK.hgt)), sx1 = Math.round(PX(KIOSK.d + 2.4, KIOSK.L + KIOSK.hw));
  const TEAL = ['#08140f', '#0e2019', '#163026', '#1e4234', '#2a5644', '#3a6c54'];
  for (let y = top; y < b; y++) {
    for (let x = x0; x < x1; x++) {
      let col = pick(TEAL, 0.5 - (y - top) / 60 + (x % 5 === 0 ? -0.15 : 0), x, y);           // painted steel panels
      if (y >= top + 5 && y < top + 16 && x > x0 + 2 && x < x1 - 3) {                            // the serving hatch, a tube light inside
        const i = x - x0, j = y - top - 5;
        col = j < 1 ? '#f0fff0' : pick(['#6a4a2a', '#a07040', '#d8a868', '#f0d8a0'], 0.85 - j / 14, x, y);
        if (j >= 2 && j < 5 && i % 4 === 1) col = ['#e8c040', '#c84a3a', '#e0e0e0'][(i >> 2) % 3];      // jars of biscuits and rusks
        if (j === 5) col = '#5a3a20';
      }
      if (y >= top + 16 && y < top + 18 && x >= x0 && x < x1) col = y === top + 16 ? '#c8c0b0' : '#6a625a';   // the counter
      if (y >= top + 19 && y < top + 25 && x > x0 + 3 && x < x1 - 4) {                           // a red menu board, glyph-like lines
        const j = y - top - 19, i = x - x0 - 4;
        col = '#8a1a1e';
        if ((j === 1 || j === 4) && i % 9 < 7) col = '#f0e0c0';                                   // the headline of each word
        if ((j === 2 || j === 3 || j === 5) && hash(i, j, 541) < 0.3 && i % 9 < 7) col = '#f0e0c0';
      }
      p.set(x, y, col);
    }
    const ys = Math.round(PY(KIOSK.d + 2.4, 0) - (b - y) * (KIOSK.d / (KIOSK.d + 2.4)));
    for (let x = x1; x < sx1; x++) if (y >= top + 1) p.set(x, y, pick(TEAL, 0.25 - (y - top) / 80, x, y));   // its south side, receding
  }
  for (let x = x0 - 2; x < sx1 + 1; x++) { p.set(x, top - 1, '#6a2a22'); p.set(x, top - 2, '#8a3a2a'); p.set(x, top, '#2a100e'); }   // tin awning
  for (let x = x0 - 1; x < x1 + 1; x++) { const u = (x - x0 + 1) / (x1 - x0 + 2); p.set(x, Math.round(top + 1 + Math.sin(u * Math.PI * 3) ** 2 * 2), x % 2 ? '#ff9a1a' : '#ffc23a'); }   // marigold garland
  return trim(p);
}
function renderSignFrame() {                            // the sign's posts, its frame and the sockets of its bulbs (billboard only)
  const p = pix(W, H), top = Math.round(PY(KIOSK.d, KIOSK.hgt)) - 2;
  for (const x of [BB.x + 8, BB.x + BB.w - 9]) for (let y = BB.y + BB.h + 1; y < top; y++) { p.set(x, y, '#1a1418'); p.set(x + 1, y, '#3a3034'); }
  for (let x = BB.x - 2; x < BB.x + BB.w + 2; x++) for (const y of [BB.y - 2, BB.y - 1, BB.y + BB.h, BB.y + BB.h + 1]) p.set(x, y, y === BB.y - 2 || y === BB.y + BB.h + 1 ? '#3a1a12' : '#6a2a1a');
  for (let y = BB.y - 2; y < BB.y + BB.h + 2; y++) for (const x of [BB.x - 2, BB.x - 1, BB.x + BB.w, BB.x + BB.w + 1]) p.set(x, y, x === BB.x - 2 || x === BB.x + BB.w + 1 ? '#3a1a12' : '#6a2a1a');
  return trim(p);
}
const BULBS = [];
for (let x = BB.x - 1; x <= BB.x + BB.w; x += 3) { BULBS.push({ x, y: BB.y - 2 }); BULBS.push({ x, y: BB.y + BB.h + 1 }); }
/* the ad: GARAM CHAI, a steaming kulhad (the clay cup chai comes in) on deep maroon, a sprig of cardamom */
const AD_BG = ['#1e050c', '#2e0812', '#420c18', '#58121e', '#6e1a24'];
function renderAd() {
  const p = pix(BB.w, BB.h), m = pix(BB.w, BB.h);
  for (let j = 0; j < BB.h; j++) for (let i = 0; i < BB.w; i++) {
    const v = 0.2 + 0.55 * Math.max(0, 1 - Math.hypot((i - 12) / 30, (j - 6) / 20)) + (((i + j * 2) % 11 === 0) ? 0.08 : 0);
    p.set(i, j, pickFlat(AD_BG, v, i, j));
  }
  for (let i = 0; i < BB.w; i++) { p.set(i, 0, '#ff9a2a'); p.set(i, BB.h - 1, '#ff9a2a'); }   // saffron edge stripes
  for (let j = 8; j < 18; j++) {                                                            // the kulhad: terracotta, wider at the lip
    const hw = 4.6 - (j - 8) * 0.22;
    for (let i = Math.round(7.5 - hw); i <= Math.round(7.5 + hw); i++) {
      const u = (i - (7.5 - hw)) / (2 * hw);
      p.set(i, j, j === 8 ? '#e8c0a0' : j === 9 ? '#c89060' : u < 0.3 ? '#d8703a' : u < 0.7 ? '#b0502a' : '#7a3018');
      m.set(i, j, '#ffffff');
    }
  }
  for (let i = 4; i <= 11; i++) p.set(i, 9, i < 7 ? '#d8a878' : '#b87e50');                 // the chai's milky surface
  p.set(13, 13, '#6a9a3a'); p.set(14, 12, '#8ac04a'); p.set(13, 12, '#4a7a2a'); p.set(14, 14, '#4a7a2a');   // cardamom pods
  const txt = (s, x, y, sc, col, sh) => { if (sh) textP(p, s, x + 1, y + 1, sh, sc); textP(p, s, x, y, col, sc); textP(m, s, x, y, '#ffffff', sc); };
  txt('GARAM', 17, 2, 1, '#ffb84a', '#1a0408');
  txt('CHAI', 15, 8, 2, '#fff0d4', '#1a0408');
  return { c: p.done(), mask: m.done() };
}
const STEAM = [{ x: 5, k: 37, ph: 0.1 }, { x: 8, k: 43, ph: 0.5 }, { x: 11, k: 31, ph: 0.8 }];
const BB_FLICK = [[64.2, 65.4], [181.5, 182.2]];
function drawBillboard(t, pitch) {
  const off = inEvent(t, BB_FLICK) && hash(9, Math.floor(t * 18), 2) < 0.5, lv = (off ? 0.6 : 1) * (0.95 + 0.05 * wave(t, 1100, 0.29));
  halo(HL.bbGlow, BB.x + BB.w / 2, BB.y + BB.h / 2, lv);
  blit(SIGN_C);
  if (!BB_PITCH.covers(t, pitch)) {
    ctx.drawImage(AD.c, BB.x, BB.y);
    for (const st of STEAM) for (let k = 0; k < 6; k++) {                                    // steam curling up from the cup
      const u = frac(t / 3.2 + st.ph + k / 6), y = BB.y + 7 - u * 7, x = BB.x + st.x + Math.sin(u * 5 + TAU * st.k * t / LOOP) * 1.2;
      if (y < BB.y + 1) continue;
      ctx.globalAlpha = (1 - u) * 0.55; rect(ctx, Math.round(x), Math.round(y), 1, 1, '#f4ece4');
    }
    ctx.globalAlpha = 1;
    const f = frac(t / 16);                                                                  // a gloss sheen rolls across
    if (f < 0.1) {
      const bx = lerp(-10, BB.w + 10, f / 0.1);
      for (let j = 0; j < BB.h; j++) {
        const sx = Math.round(bx - j * 0.6), a = Math.max(0, sx), b2 = Math.min(BB.w, sx + 3);
        if (b2 <= a) continue;
        ctx.globalAlpha = 0.12; rect(ctx, BB.x + a, BB.y + j, b2 - a, 1, '#ffffff');
        ctx.globalAlpha = 0.5; ctx.drawImage(AD.mask, a, j, b2 - a, 1, BB.x + a, BB.y + j, b2 - a, 1);
      }
      ctx.globalAlpha = 1;
    }
    if (off) { ctx.globalAlpha = 0.3; rect(ctx, BB.x, BB.y, BB.w, BB.h, '#08040c'); ctx.globalAlpha = 1; }
  }
  BB_PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  const ch = step(t + 0.13, 720);                                                                   // the bulbs chase round the frame
  BULBS.forEach((bl, i) => {
    const on = !off && ((i >> 1) + ch) % 3 !== 0;
    rect(ctx, bl.x, bl.y, 1, 1, on ? '#fff0b0' : '#6a4a2a');
    if (on && i % 4 === 0) halo(HL.bulb, bl.x, bl.y, 0.7);
  });
}
/** the kiosk: its tube light, the chai-wallah pouring from a height, the kettle's steam, customers with cups */
const POURS = [[12, 15], [48, 51.5], [97, 100], [139, 142.5], [176, 179], [221, 224.5]];
function drawKiosk(t) {
  blit(KIOSK_C);
  const s = KIOSK.s, x0 = Math.round(KIOSK.cx - KIOSK.hw * s), top = Math.round(PY(KIOSK.d, KIOSK.hgt));
  const wx = x0 + 17, wy = top + 7;                                                         // the chai-wallah behind the counter
  rect(ctx, wx, wy + 2, 5, 7, '#e8e0cc'); rect(ctx, wx + 1, wy - 1, 3, 3, '#8a5a3a'); rect(ctx, wx + 1, wy - 2, 3, 1, '#1a1210');
  const pour = POURS.find(([a, b]) => t >= a && t < b);
  if (pour) {                                                                               // the long pour between two glasses
    const u = (t - pour[0]) / (pour[1] - pour[0]), hgt = Math.round(3 + 3 * Math.sin(Math.PI * Math.min(1, u * 1.2)));
    rect(ctx, wx - 2, wy + 7 - hgt, 2, 2, '#c8ccd0'); rect(ctx, wx + 5, wy + 7 - hgt, 1, 1, '#e8c098');
    for (let k = 0; k < hgt + 1; k++) if ((k + Math.floor(t * 20)) % 3) rect(ctx, wx - 1, wy + 9 - hgt + k, 1, 1, '#d09860');
    rect(ctx, wx - 2, wy + 9, 2, 2, '#e8e8e0');
  } else { rect(ctx, wx - 1, wy + 5, 2, 2, '#c8ccd0'); rect(ctx, wx + 5, wy + 5, 1, 2, '#e8c098'); }
  rect(ctx, x0 + 6, top + 12, 4, 3, '#a8aab0'); rect(ctx, x0 + 7, top + 11, 2, 1, '#6a6c72');   // the kettle on its stove
  rect(ctx, x0 + 6, top + 15, 4, 1, Math.floor(t * 9 + 0.5) & 1 ? '#5a8aff' : '#3a6ae8');
  for (let k = 0; k < 4; k++) { const u = frac(t / 2.4 + k / 4); ctx.globalAlpha = (1 - u) * 0.5; rect(ctx, Math.round(x0 + 8 + Math.sin(u * 6 + k) * 1.5), Math.round(top + 11 - u * 9), 1, 1, '#e8e8f0'); }
  ctx.globalAlpha = 1;
  halo(HL.tube, KIOSK.cx, top + 6, 0.9);
  // two customers with glasses of chai, one sips
  const cb = KIOSK.base + 1, sip = frac(t / 8 + 0.5) < 0.3;
  for (const [x, shirt, lift] of [[x0 - 5, '#3a5a9a', sip], [x0 + 3, '#c89a3a', !sip && frac(t / 10 + 0.5) < 0.25]]) {
    rect(ctx, x, cb - 17, 4, 10, shirt); rect(ctx, x, cb - 7, 1, 7, '#1c1a24'); rect(ctx, x + 3, cb - 7, 1, 7, '#1c1a24'); rect(ctx, x + 1, cb - 7, 2, 1, '#1c1a24');
    rect(ctx, x + 1, cb - 20, 3, 3, '#8a5a3a'); rect(ctx, x + 1, cb - 21, 3, 1, '#141010');
    rect(ctx, x + 4, lift ? cb - 18 : cb - 13, 1, 2, '#e8c098');
  }
}

/* ================= People: small painted figures (lit from the lamps on their left) ================= */
const SKIN = ['#4a2a1c', '#7a4a30', '#a86e48', '#c88c60'];
/** a figure seated cross-legged, seen from behind: cx, base (row of the ground), s px/m */
function paintSeated(p, cx, base, s, o) {
  const hw = 0.2 * s, torsoH = 0.5 * s, headR = 0.105 * s, lapW = 0.33 * s, lapH = Math.max(2, 0.16 * s);
  const tTop = base - lapH - torsoH, hy = tTop - headR * 1.1;
  for (let y = Math.floor(base - lapH); y < base; y++) for (let x = Math.floor(cx - lapW); x <= Math.ceil(cx + lapW); x++) {
    const u = (x - cx) / lapW;
    if (Math.abs(u) > 1 - ((y - (base - lapH)) / lapH) * 0.1) continue;
    p.set(x, y, u < -0.6 ? o.low[0] : o.low[1]);
  }
  for (let y = Math.floor(tTop); y < base - lapH; y++) {
    const f = (y - tTop) / torsoH, w = hw * (f < 0.12 ? 0.78 + f * 1.8 : f < 0.7 ? 1 - (f - 0.12) * 0.25 : 0.855 + (f - 0.7) * 0.4);
    for (let x = Math.floor(cx - w); x <= Math.ceil(cx + w); x++) {
      const u = (x - cx) / w;
      let c = u < -0.72 ? o.top[0] : u > 0.55 ? o.top[2] : o.top[1];
      if (s > 18 && Math.abs(u + 0.4) < 0.12 && f > 0.1 && f < 0.8) c = o.top[0];                // lamp light along the left shoulder blade
      if (s > 18 && Math.abs(u) < 0.06 && f > 0.15 && !o.pallu) c = o.top[2];                     // the spine's crease
      if (s > 18 && f > 0.62 && f < 0.7 && Math.abs(u) < 0.9 && o.belt) c = o.belt;
      p.set(x, y, c);
    }
  }
  if (s > 12) for (const sd of [-1, 1]) for (let y = Math.floor(tTop + 2); y < base - lapH; y++) p.set(Math.round(cx + sd * (hw + 0.5)), y, sd < 0 ? o.top[0] : o.top[2]);   // arms
  if (o.pallu) for (let y = Math.floor(tTop); y < base - lapH; y++) { const x = Math.round(cx - hw * 0.1 + (y - tTop) * 0.35); p.set(x, y, o.pallu); p.set(x + 1, y, o.pallu); }   // the saree's border across the back
  disc(p, cx, hy, headR, (dx, dy) => (dy > 0.55 && Math.abs(dx) < 0.5 ? SKIN[1] : dx < -0.5 ? o.hairLit : o.hair));
  if (o.bun) disc(p, cx + headR * 0.2, hy + headR * 0.7, headR * 0.5, o.hair);
  for (let y = Math.round(hy + headR); y < tTop + 1; y++) p.set(cx, y, SKIN[1]);
}
/** a standing figure facing us: shirt or kurta over trousers; o.step lifts one foot (walking frames) */
function paintStanding(p, cx, base, s, hgt, o) {
  const H0 = hgt * s, headH = Math.max(2, H0 / 7.2), headW = Math.max(2, headH * 0.8), shW = Math.max(1.5, 0.125 * H0), wW = Math.max(1.2, 0.095 * H0);
  const top = base - H0, sh = top + headH + Math.max(1, 0.02 * H0), hip = base - 0.5 * H0, hem = o.kurta ? base - 0.27 * H0 : hip;
  const shade = (u, ramp) => (u < -0.55 ? ramp[0] : u > 0.5 ? ramp[2] : ramp[1]);
  for (let y = Math.floor(sh); y < hem; y++) {                                                     // shirt / kurta
    const f = (y - sh) / Math.max(1, hip - sh), w = y < sh + 1 ? shW * 0.8 : y < hip ? lerp(shW, wW, f) : wW * (1 + (y - hip) / H0 * 0.9);
    for (let x = Math.floor(cx - w); x <= Math.ceil(cx + w); x++) p.set(x, y, shade((x - cx) / w, o.top));
  }
  const lw = Math.max(1, Math.round(0.05 * H0)), gap = Math.max(1, Math.round(0.03 * H0));
  for (let y = Math.floor(hem); y < base; y++) {                                                   // legs and feet
    if (!(o.step && y >= base - 1)) p.fill(Math.round(cx - gap / 2 - lw), y, lw, 1, o.low[0]);
    p.fill(Math.round(cx + gap / 2), y, lw, 1, o.low[1]);
  }
  p.fill(Math.round(cx - gap / 2 - lw) - (H0 > 24 ? 1 : 0), base - (o.step ? 2 : 1), lw + (H0 > 24 ? 1 : 0), 1, '#141014');
  p.fill(Math.round(cx + gap / 2), base - 1, lw + (H0 > 24 ? 1 : 0), 1, '#141014');
  for (const sd of [-1, 1]) {                                                                      // arms, hands
    const ax = Math.round(cx + sd * (shW + 0.8));
    for (let y = Math.floor(sh + 1); y < hip + 0.06 * H0; y++) p.set(ax, y, y > hip - 0.02 * H0 ? (sd < 0 ? SKIN[3] : SKIN[1]) : sd < 0 ? o.top[0] : o.top[2]);
  }
  const hy = top + headH / 2;
  for (let y = Math.floor(top); y < Math.ceil(top + headH); y++) for (let x = Math.floor(cx - headW / 2); x <= Math.ceil(cx + headW / 2); x++) {
    const dx = (x - cx) / (headW / 2), dy = (y - hy) / (headH / 2);
    if (dx * dx + dy * dy > 1.15) continue;
    p.set(x, y, dy < -0.3 || Math.abs(dx) > 0.8 && dy < 0.3 ? o.hair : dx < -0.3 ? SKIN[3] : SKIN[2]);
  }
  if (H0 > 30) { p.set(Math.round(cx - headW * 0.22), Math.round(hy), '#1a100c'); p.set(Math.round(cx + headW * 0.22), Math.round(hy), '#1a100c'); }
  return { hy, headR: headH / 2, tTop: sh };
}

/* ================= Saptaparni (Alstonia scholaris): a straight trunk, broad layers of leaf whorls, cream umbels at their tips ================= */
// The crown spreads rather than tapers: the layers stay wide to the top and nearly level, each whorl a fan of glossy
// leaves lit from the lamps on the left, and each holds a small dense clump of florets just above its outer tip.
const SAP_LEAF = ['#050c08', '#0a1810', '#11261a', '#1a3822', '#264c2a', '#346032', '#46723a'];
const SAP_BLOOM = ['#5e6248', '#8e9270', '#b8ba94', '#dcdab8', '#f0ecd2'];
function renderSapta(q) {
  const p = pix(W, H), r = rng(600 + q.seed), s = F / q.d, cx = PX(q.d, q.L), base = PY(q.d), tw = Math.max(3, Math.round(0.42 * s));
  const tiers = [[3.8, 3.1], [6.0, 3.6], [8.2, 3.4], [10.2, 2.9], [12.0, 2.2]];
  for (let y = Math.max(0, Math.round(PY(q.d, 12.6))); y < Math.round(base); y++) for (let i = 0; i < tw; i++) {   // the trunk: pale grey bark
    const u = i / (tw - 1);
    p.set(Math.round(cx) - (tw >> 1) + i, y, pick(['#141214', '#262224', '#3a3434', '#524a46', '#6e6258'], 0.45 - u * 0.35 + (u < 0.3 ? 0.12 : 0) + (hash(i, y >> 1, 601) - 0.5) * 0.18, cx + i, y));
  }
  for (const [h, R] of tiers) {
    const ty = PY(q.d, h), n = Math.round(R * 4.4), ros = [];
    for (let k = 0; k < n; k++) {
      const u = ((k + 0.5) / n) * 2 - 1 + (r() - 0.5) * 0.12, back = k % 2 === 1;
      ros.push({ x: cx + u * R * s, y: ty - (1 - u * u) * 0.14 * s - u * u * 0.08 * s + (back ? -0.26 * s : 0.06 * s) + (r() - 0.5) * 0.14 * s,
        w: (0.44 + r() * 0.16) * s, h: (0.21 + r() * 0.08) * s, back, u });
    }
    ros.sort((a, b) => (a.back === b.back ? 0 : a.back ? -1 : 1));
    for (const ro of ros) if (!ro.back) line(p, cx, ty + 0.22 * s, lerp(cx, ro.x, 0.85), ro.y + ro.h * 0.2, '#221c1c');   // the whorl's branches
    for (const ro of ros) {
      for (let y = Math.floor(ro.y - ro.h - 1); y <= Math.ceil(ro.y + ro.h + 1); y++) for (let x = Math.floor(ro.x - ro.w - 1); x <= Math.ceil(ro.x + ro.w + 1); x++) {
        const dx = (x - ro.x) / ro.w, dy = (y - ro.y) / ro.h, e = dx * dx + dy * dy;
        if (e > 1 + (vnoise(x / 1.3, y / 1.3, 610 + q.seed) - 0.5) * 0.5) continue;
        const rib = Math.sin(Math.atan2(dy, dx * 0.5) * 7) > 0.55;                                    // the leaves of the rosette fanning out
        p.set(x, y, pick(SAP_LEAF, 0.36 + (1 - dy) * 0.16 + (dx < -0.35 ? 0.1 : 0) - (rib ? 0.16 : 0) - (ro.back ? 0.14 : 0) + (hash(x, y, 612) - 0.5) * 0.12, x, y));
      }
      // an umbel of florets just above the whorl's outer tip (over the middle for the whorls by the trunk), some with a second
      const out = Math.abs(ro.u) < 0.15 ? 0 : Math.sign(ro.u), clumps = r() < 0.4 ? 2 : 1;
      for (let k = 0; k < clumps; k++) {
        const bx = ro.x + (k ? -0.25 * out : out * 0.62) * ro.w + (r() - 0.5) * 0.12 * s, by = ro.y - ro.h * (k ? 0.8 : 0.6), br = (k ? 0.08 : 0.11) * s;
        for (let y = Math.floor(by - br); y <= Math.ceil(by + br * 0.7); y++) for (let x = Math.floor(bx - br); x <= Math.ceil(bx + br); x++) {
          const dx = (x - bx) / br, dy = (y - by) / (br * 0.7);
          if (dx * dx + dy * dy > 1.15 || hash(x, y, 630 + q.seed) < 0.18) continue;
          p.set(x, y, pick(SAP_BLOOM, 0.62 - dy * 0.22 - (dx > 0.4 ? 0.12 : 0) - (ro.back ? 0.22 : 0) + (hash(x, y, 631) - 0.5) * 0.24, x, y));
        }
      }
    }
  }
  return trim(p);
}

/* ================= The roast-corn seller: a hand-cart, an iron brazier of glowing coals, cobs, a hand fan ================= */
function renderBhutta() {
  const p = pix(W, H), s = F / BHUTTA.d, cx = PX(BHUTTA.d, BHUTTA.L), b = Math.round(PY(BHUTTA.d));
  const w = Math.round(1.1 * s), deck = Math.round(b - 0.72 * s), x0 = Math.round(cx - w / 2);
  const WOOD = ['#1a0e0a', '#2e1a10', '#4a2c18', '#6a4222', '#8a5a30'];
  for (let y = deck; y < deck + 3; y++) for (let x = x0; x < x0 + w; x++) p.set(x, y, pick(WOOD, y === deck ? 0.8 : 0.45, x, y));
  for (let y = deck + 3; y < deck + 8; y++) for (let x = x0 + 2; x < x0 + w - 2; x++) p.set(x, y, x % 4 === 0 ? WOOD[0] : pick(WOOD, 0.3, x, y));
  for (const wx of [x0 + 6, x0 + w - 7]) disc(p, wx, b - 0.24 * s, 0.24 * s, (dx, dy) => Math.hypot(dx, dy) > 0.72 ? '#0e0a0a' : Math.abs(dx) < 0.15 || Math.abs(dy) < 0.15 ? '#5a4030' : '#1a1210');
  for (let y = deck - 5; y < deck; y++) for (let x = x0 + 5; x < x0 + 18; x++) {                 // the brazier (angithi), iron, on the deck
    const u = (x - x0 - 5) / 13;
    if (y < deck - 3 && (u < 0.08 || u > 0.92)) continue;
    p.set(x, y, y === deck - 5 ? '#4a3a34' : u < 0.25 ? '#3a2e2a' : '#1e1818');
  }
  for (let y = deck - 6; y < deck; y++) for (let x = x0 + w - 9; x < x0 + w - 2; x++) p.set(x, y, (x + y) % 3 ? '#4a6a2a' : '#6a8a3a');   // a basket of cobs in husk
  for (let x = x0 + w - 9; x < x0 + w - 2; x++) p.set(x, deck - 7, '#8a6a3a');
  // the seller squats on a low stool at the cart's end, in profile, facing his coals: vest, lungi, a checked towel
  const vx = x0 - 6, vb = b - 1, S = v => Math.round(v * s);
  p.fill(vx - S(0.18), vb - S(0.28), S(0.34), S(0.28), '#3a2a22');                                 // the stool
  for (let y = vb - S(0.62); y < vb - S(0.28); y++) for (let x = vx - S(0.14); x < vx + S(0.32); x++) p.set(x, y, x > vx + S(0.2) ? '#2a3a5a' : '#3a4a70');   // lungi over the knees
  for (let y = vb - S(0.28); y < vb; y++) p.fill(vx + S(0.2), y, S(0.08), 1, '#7a4a30');            // shins
  p.fill(vx + S(0.18), vb - 1, S(0.14), 1, '#4a2a1c');
  for (let y = vb - S(1.02); y < vb - S(0.58); y++) {                                              // the vest, leaning forward
    const lean = (vb - S(0.58) - y) * 0.18;
    for (let x = Math.round(vx - S(0.13) + lean); x < Math.round(vx + S(0.14) + lean); x++) p.set(x, y, x < vx - S(0.06) + lean ? '#fff8ec' : '#d8d0c0');
  }
  for (let k = 0; k < S(0.36); k++) p.set(vx - S(0.1) + k * 0.7 + 3, vb - S(1.0) + k, (k >> 1) % 2 ? '#c83a3a' : '#f0e0c8');   // the towel over his shoulder
  const hx = Math.round(vx + 0.2 * S(1)), hy = vb - S(1.2);
  disc(p, hx, hy, S(0.1), (dx, dy) => dy < -0.3 || dx < -0.5 ? '#141010' : dx > 0.4 ? '#8a5a3a' : '#a86e48');
  p.set(hx + S(0.1) + 1, hy + 1, '#a86e48');                                                        // his nose
  for (let k = 0; k < S(0.3); k++) p.set(vx + S(0.12) + k, vb - S(0.9) + k * 0.4, '#8a5a3a');     // the arm reaching to the coals

  return { ...trim(p), deck, x0, w };
}
const FAN = [[20.5, 26], [55, 61], [102, 107.5], [148, 154], [193, 199], [228, 233]];       // he fans the coals now and then
function drawBhutta(t) {
  const B = BHUTTA_C, fanning = inEvent(t, FAN);
  blit(B);
  const cy = B.deck - 4, x0 = B.x0 + 6;
  const glow = (fanning ? 1 : 0.7) * (0.85 + 0.15 * hash(3, Math.floor(t * 8 + 0.5), 5));
  for (let x = 0; x < 11; x++) {                                                                  // coals under the cobs
    const hv = hash(x, Math.floor(t * 6 + 0.5), 7);
    rect(ctx, x0 + x, cy, 1, 1, hv * glow > 0.6 ? '#ffd24a' : hv * glow > 0.3 ? '#ff7a1a' : '#b8300e');
  }
  for (let k = 0; k < 3; k++) { rect(ctx, x0 + 1 + k * 3, cy - 1, 3, 1, '#e8b830'); rect(ctx, x0 + 2 + k * 3, cy - 1, 1, 1, '#5a3010'); }   // cobs, charred
  halo(HL.coals, x0 + 5, cy, glow);
  drawPool(POOL.coals, x0 + 5, Math.round(PY(BHUTTA.d)) + 1, glow * 0.8);
  const fy = cy - 3 - (fanning ? (Math.floor(t * 7) & 1) * 3 : 0);                               // the hand fan over the coals
  rect(ctx, B.x0 + 2, fy, 4, 3, '#d8b070'); rect(ctx, B.x0 + 2, fy + 1, 4, 1, '#a07a48'); rect(ctx, B.x0 + 1, fy + 2, 1, 2, '#8a5a3a');
  for (let k = 0; k < (fanning ? 9 : 4); k++) {                                                   // sparks
    const { cyc, u } = cycle(t, fanning ? 1900 + k * 13 : 700 + k * 29, k * 0.13);
    const x = x0 + 2 + hash(k, cyc, 8) * 8 + Math.sin(u * 7 + k) * 2, y = cy - 1 - u * (10 + hash(k, cyc, 9) * 12);
    ctx.globalAlpha = 1 - u; rect(ctx, Math.round(x), Math.round(y), 1, 1, u < 0.4 ? '#ffe070' : '#ff8a2a');
  }
  for (let k = 0; k < 5; k++) {                                                                   // smoke drifting off to the right
    const u = frac(t / 5 + k / 5), x = x0 + 5 + u * 16 + Math.sin(u * 5 + k) * 2, y = cy - 3 - u * 26;
    ctx.globalAlpha = (1 - u) * 0.18; rect(ctx, Math.round(x), Math.round(y), 2 + Math.round(u * 3), 1, '#a8a0a8');
  }
  ctx.globalAlpha = 1;
}

/* ================= The kulfi cart: a red-wrapped matka on a wooden cart, a small LED on a stick ================= */
function renderKulfi() {
  const p = pix(W, H), s = F / KULFI.d, cx = PX(KULFI.d, KULFI.L), b = Math.round(PY(KULFI.d)), w = Math.round(1.3 * s), x0 = Math.round(cx - w / 2);
  const deck = Math.round(b - 0.75 * s);
  for (let x = x0; x < x0 + w; x++) { p.set(x, deck, '#8a5a30'); p.set(x, deck + 1, '#4a2c18'); for (let y = deck + 2; y < deck + 5; y++) p.set(x, y, x % 3 ? '#2e1a10' : '#1a0e0a'); }
  for (const wx of [x0 + 3, x0 + w - 4]) { p.fill(wx - 1, b - 3, 3, 3, '#0e0a0a'); p.set(wx, b - 2, '#5a4030'); }
  disc(p, cx, deck - 0.3 * s, 0.28 * s, (dx, dy) => dy < -0.5 ? '#f0e0c8' : dx < -0.3 ? '#e04038' : '#a82a2a');   // the matka under its red cloth
  for (let y = Math.round(deck - 1.5 * s); y < deck; y++) p.set(x0 + w - 1, y, '#3a3036');
  paintStanding(p, x0 - 3, b, s, 1.65, { top: ['#c8c4b8', '#f0ece0', '#a8a498'], low: ['#e8e4d8', '#c8c4b8'], hair: '#141010', kurta: true });
  return { ...trim(p), led: { x: x0 + w - 1, y: Math.round(deck - 1.5 * s) } };
}

/* ================= The balloon seller: a bamboo stick with a bunch of balloons (some light up) ================= */
const BALLOONS = [];
{
  const r = rng(700), cols = [['#ff3a4a', '#a81a2a'], ['#ff7ac8', '#b83a8a'], ['#ffd23a', '#b8901a'], ['#4ac86a', '#1a8a3a'], ['#4a8aff', '#1a4ab8'], ['#e8e8f0', '#8a8a98']];
  for (let k = 0; k < 17; k++) {
    const a = r() * TAU, rr = Math.sqrt(r());
    BALLOONS.push({ dx: Math.cos(a) * rr * 13, dy: -Math.abs(Math.sin(a)) * rr * 11 - 4, col: cols[k % 6], heart: k % 5 === 2, led: k % 6 === 4 || k === 9, k: 20 + Math.floor(r() * 30), ph: r() });
  }
  BALLOONS.sort((a, b) => a.dy - b.dy);
}
function renderBalloonMan() {
  const p = pix(W, H), s = F / BALLOON.d, cx = Math.round(PX(BALLOON.d, BALLOON.L)), b = Math.round(PY(BALLOON.d));
  const fig = paintStanding(p, cx, b, s, 1.66, { top: ['#6a2a2a', '#b84a3a', '#7a2a2a'], low: ['#2a2a3a', '#1c1c28'], hair: '#1a1210' });
  for (let x = cx - 3; x <= cx + 3; x++) if ((x + b) % 2) p.set(x, Math.round(fig.tTop + 3), '#e8c050');   // a checked shirt
  const top = Math.round(PY(BALLOON.d, 2.05));
  for (let y = top; y < Math.round(fig.tTop + 0.4 * s); y++) p.set(cx + 5, y, y > fig.tTop ? '#a8844a' : '#c89a5a');   // the bamboo pole
  return { ...trim(p), pole: { x: cx + 5, y: top }, hand: { x: cx - 5, y: Math.round(fig.tTop + 2) } };
}
function drawBalloons(t) {
  const P = BALLOON_C.pole, sw = wave(t, 7, 0.2) * 2 + wave(t, 19, 0.5);
  ctx.fillStyle = '#c8c0b0';
  for (const bl of BALLOONS) {                                                                    // strings to the pole's top
    const x = P.x + bl.dx + sw * (1 + bl.dy / -15), y = P.y + bl.dy;
    for (let k = 1; k < 4; k++) ctx.fillRect(Math.round(lerp(P.x, x, k / 4)), Math.round(lerp(P.y, y + 4, k / 4)), 1, 1);
  }
  for (const bl of BALLOONS) {
    const x = Math.round(P.x + bl.dx + sw * (1 + bl.dy / -15) + wave(t, bl.k, bl.ph) * 0.8), y = Math.round(P.y + bl.dy + wave(t, bl.k + 3, bl.ph) * 0.6);
    const spr = bl.heart ? BAL_SPR.heart[bl.col[0]] : BAL_SPR.round[bl.col[0]];
    ctx.drawImage(spr, x - 3, y - 3);
    if (bl.led) {                                                                                  // an LED balloon cycling its colours
      const c = ['#ff5ad8', '#5ae8ff', '#fff05a', '#7aff7a'][step(t + bl.ph * 3, 160 + bl.k) % 4];
      rect(ctx, x - 1, y - 1, 3, 3, c); halo(HL.ledBal, x, y, 0.8);
    }
  }
}
function makeBalloon(col, heart) {
  const p = pix(7, 8);
  for (let y = 0; y < 7; y++) for (let x = 0; x < 7; x++) {
    const dx = x - 3, dy = y - 3;
    const inside = heart ? (y < 3 ? Math.hypot(Math.abs(dx) - 1.6, dy + 1) < 1.9 : Math.abs(dx) <= 3 - (y - 3) * 0.9) : dx * dx / 10 + dy * dy / 11 < 1;
    if (!inside) continue;
    p.set(x, y, dx + dy < -2 ? mixHex(col[0], '#ffffff', 0.45) : dx + dy > 2 ? col[1] : col[0]);
  }
  p.set(3, 7, col[1]);
  return p.done();
}

/* ================= A family on a striped durrie: tiffin, a kid in glowing bunny ears, a spinning light toy ================= */
const FAM = {
  dad: { L: FAMILY.L + 0.45, d: FAMILY.d + 0.3 }, mum: { L: FAMILY.L + 1.85, d: FAMILY.d + 0.5 },
  kid: { L: FAMILY.L + 1.15, d: FAMILY.d - 0.4 }, tot: { L: FAMILY.L + 2.4, d: FAMILY.d - 0.6 },
};
for (const k in FAM) { const m = FAM[k]; m.x = PX(m.d, m.L); m.b = PY(m.d); m.s = F / m.d; }
function renderFamily() {
  const p = pix(W, H);
  for (let y = Math.round(PY(FAMILY.d + 1.6)); y < Math.round(PY(FAMILY.d - 1.3)); y++) {        // the durrie, in perspective
    const d = F * EYE / (y + 0.5 - HZ);
    for (let x = Math.round(PX(d, FAMILY.L - 0.1)); x < Math.round(PX(d, FAMILY.L + 2.7)); x++) {
      const L = (x + 0.5 - VPX) * d / F, band = ((Math.floor((L - FAMILY.L) / 0.28) % 3) + 3) % 3;
      p.set(x, y, pick([['#1a2a5a', '#2a3e7a', '#3a52a0'], ['#6a1a24', '#8a2a30', '#b03a3a'], ['#8a7a5a', '#b0a07a', '#d0c098']][band], 0.4 + (PX(d, 0) - x) * -0.002, x, y));
    }
  }
  const D = FAM.dad, M = FAM.mum, T = FAM.tot;
  paintSeated(p, D.x, D.b, D.s, { top: ['#f4f0e4', '#d8d2c4', '#8a8478'], low: ['#3a3440', '#2a2632'], hair: '#141010', hairLit: '#4a3424' });
  paintSeated(p, M.x, M.b, M.s, { top: ['#ff5ab0', '#c8287a', '#7a1450'], low: ['#c8287a', '#7a1450'], hair: '#0e0a0a', hairLit: '#3a2418', pallu: '#f0c040', bun: true });
  paintSeated(p, T.x, T.b, T.s * 0.72, { top: ['#8ae0a0', '#4ab86a', '#2a7a44'], low: ['#2a3a6a', '#1a2448'], hair: '#141010', hairLit: '#4a3424' });
  const tx = Math.round(PX(FAMILY.d - 0.5, FAMILY.L + 0.55)), ty = Math.round(PY(FAMILY.d - 0.5));   // a steel tiffin carrier, three tiers
  for (let j = 0; j < 9; j++) for (let i = 0; i < 7; i++) p.set(tx + i, ty - 9 + j, j % 3 === 0 ? '#5a5e66' : i < 2 ? '#e8ecf0' : i < 5 ? '#a8aeb8' : '#6a707a');
  p.fill(tx + 1, ty - 12, 1, 3, '#8a8e96'); p.fill(tx + 5, ty - 12, 1, 3, '#8a8e96'); p.fill(tx + 1, ty - 12, 5, 1, '#8a8e96');
  return trim(p);
}
function drawFamily(t) {
  blit(FAMILY_C);
  const K = FAM.kid, s = K.s, hop = Math.max(0, Math.sin(TAU * 60 * t / LOOP)) > 0.7 ? 2 : 0;   // the kid bounces on the spot
  const x = Math.round(K.x), b = Math.round(K.b) - hop, hgt = Math.round(1.05 * s), hw = Math.round(0.14 * s);
  rect(ctx, x - hw, b - Math.round(hgt * 0.62), hw * 2 + 1, Math.round(hgt * 0.36), '#ffd23a');   // a yellow frock
  rect(ctx, x - hw, b - Math.round(hgt * 0.62), 1, Math.round(hgt * 0.36), '#fff07a');
  rect(ctx, x - 2, b - Math.round(hgt * 0.26), 2, Math.round(hgt * 0.26), '#a86e48'); rect(ctx, x + 1, b - Math.round(hgt * 0.26), 2, Math.round(hgt * 0.26), '#7a4a30');
  const hy = b - hgt + 4;
  rect(ctx, x - 3, hy, 7, 6, '#c88c60'); rect(ctx, x - 3, hy - 1, 7, 2, '#141010'); rect(ctx, x - 3, hy, 1, 4, '#141010'); rect(ctx, x + 3, hy, 1, 4, '#141010');
  rect(ctx, x - 1, hy + 2, 1, 1, '#1a100c'); rect(ctx, x + 1, hy + 2, 1, 1, '#1a100c');
  const wave2 = wave(t, 30, 0.2) > 0.4;                                                          // one arm waves the other way
  rect(ctx, x + hw + 1, b - Math.round(hgt * 0.6) - (wave2 ? 6 : 0), 1, 5, '#a86e48');
  const ears = 0.6 + 0.4 * wave(t, 240, 0.1), ec = ears > 0.75 ? '#ff7ae0' : '#e03ab8';        // the glowing bunny ears
  for (const ex of [x - 2, x + 2]) { rect(ctx, ex, hy - 6, 1, 5, ec); rect(ctx, ex, hy - 6, 1, 1, '#ffe0f4'); }
  halo(HL.ears, x, hy - 3, ears);
  const T = FAM.tot, sp = step(t + 0.07, 1440) % 4, tc = ['#ff4a6a', '#4ae0ff', '#ffe04a', '#8aff6a'];  // the little one's spinning light-up top
  const sx = Math.round(T.x - 9), sy = Math.round(T.b) - 3;
  rect(ctx, sx, sy, 3, 2, tc[sp]); rect(ctx, sx + 1, sy + 2, 1, 1, tc[(sp + 2) % 4]); halo(HL.top, sx + 1, sy + 1, 0.8);
}

/* ================= A couple on the canal's edge, backs to us, watching the gate ================= */
function renderCouple() {
  const p = pix(W, H), s = F / COUPLE.d, b = Math.round(PY(COUPLE.d));
  const wx = PX(COUPLE.d, COUPLE.L - 0.22), mx = PX(COUPLE.d, COUPLE.L + 0.3);
  paintSeated(p, wx, b + 1, s * 0.94, { top: ['#e85a4a', '#b8302e', '#6a1a1e'], low: ['#b8302e', '#6a1a1e'], hair: '#0e0a0a', hairLit: '#3a2418' });
  paintSeated(p, mx, b + 1, s, { top: ['#c8d4e8', '#8a9ab8', '#4a5470'], low: ['#2a2a3a', '#1c1c28'], hair: '#141010', hairLit: '#4a3424' });
  for (let y = b - Math.round(0.95 * s); y < b - Math.round(0.3 * s); y++) p.set(wx + 0.5 * s * 0.4, y + 3, '#0e0a0a');   // her long braid
  return { ...trim(p), wx: Math.round(wx), mx: Math.round(mx), b, s };
}
function drawCouple(t) {
  const C0 = COUPLE_C;
  blit(C0);
  const lean = wave(t, 4, 0.3) > 0.2;                                                            // now and then she leans on his shoulder
  if (lean) { rect(ctx, C0.wx + 1, Math.round(C0.b - 0.98 * C0.s), 2, 3, '#0e0a0a'); }
  const up = COPTERS.some(c => c.far && t > c.t0 && t < c.t0 + COP_UP + 2.5);                  // he points at a toy copter
  if (up) for (let k = 0; k < 5; k++) rect(ctx, C0.mx + 3 + k, Math.round(C0.b - 0.62 * C0.s) - k * 2, 1, 2, k < 4 ? '#8a9ab8' : '#a86e48');
}

/* ================= The dog asleep by the lamp post: breathing, an ear that twitches, a tail that flicks ================= */
const DOG_ROWS = [
  '..ee....................', '.eDDe.......dddddd......', '.DDDDd...ddDDDDDDDdd....', 'nDDDDDddDDDDDDDDDDDDd...',
  'DDDkDDDDDDDDDDDDDDDDDd..', '.DDDDDDDDDDDDDDDDDDDDDd.', 'PPPLLDDDDDDDDDDDDDDDDDd.', 'PPPLLLLLDDDDDDDDDDDTTTd.',
  '.dddLLLLLLLLLLDDDTTTTd..', '..dddddddddddddddddd....'];
function renderDog() {
  return sprite(DOG_ROWS, { D: '#b87a48', L: '#e8b880', d: '#5a3418', e: '#7a4a28', n: '#141010', k: '#3a2014', P: '#f0c890', T: '#9a6034' });
}
function drawDog(t) {
  const x = Math.round(PX(DOG.d, DOG.L)) - 12, b = Math.round(PY(DOG.d)), y = b - DOG_C.height;
  ctx.drawImage(DOG_C, x, y);
  if (wave(t, 64, 0.1) > 0) rect(ctx, x + 12, y, 6, 1, '#8a5430');                                // breathing: the flank rises
  if (frac(t / 12 + 0.3) > 0.94) rect(ctx, x + 2, y - 1, 1, 1, '#7a4a28');                             // an ear twitches
  if (inEvent(t, [[37, 38.2], [118, 119], [207, 208.4]])) rect(ctx, x + 22, y + 6, 2, 1, '#9a6034');   // the tail flicks
}

/* ================= Across the canal: families on the grass, and a pair walking towards us along the far walk ================= */
function renderFarFolk() {
  const p = pix(W, H);
  const groups = [
    { d: 50, L: -24.2, who: [['#e8e0cc', '#141010'], ['#e0287a', '#0e0a0a'], ['#4ab86a', '#141010', 0.7]] },
    { d: 61, L: -22.6, who: [['#3a5a9a', '#141010'], ['#f0c040', '#0e0a0a']] },
    { d: 44, L: -27.5, who: [['#c83a3a', '#141010'], ['#e8e0cc', '#141010'], ['#8a3a8a', '#0e0a0a']] },
  ];
  for (const g of groups) g.who.forEach(([top, hair, k = 1], i) => {
    const d = g.d + i * 0.2, L = g.L - i * 0.55, s = F / d;
    paintSeated(p, PX(d, L), PY(d), s * k, { top: [mixHex(top, '#ffe0b0', 0.3), top, mixHex(top, '#000000', 0.5)], low: ['#2a2632', '#1c1a24'], hair, hairLit: '#3a2a20' });
  });
  return trim(p);
}
// they pass the parked police van (t 29-37, 141-149) before the ice-cream tricycle does (t 40-45, 152-157)
const WALKS = [{ t0: 8, t1: 54, d0: 88, d1: 30 }, { t0: 120, t1: 166, d0: 88, d1: 30 }];
const WALK_L = -18.3;
function makeWalkers(h, fr) {
  const s = h / 1.65, w = Math.round(1.1 * s) + 4, p = pix(w, h + 2);
  paintStanding(p, Math.round(w * 0.3), h + 2, s, 1.65, { top: ['#f4f0e4', '#d0cabc', '#8a8478'], low: ['#4a4a5a', '#2a2a36'], hair: '#141010', kurta: true, step: fr });
  paintStanding(p, Math.round(w * 0.72), h + 2, s * 0.94, 1.65, { top: ['#ffe07a', '#e8a828', '#8a5a10'], low: ['#e0a020', '#8a5a10'], hair: '#0e0a0a', kurta: true, step: !fr });
  return p.done();
}
function drawWalkers(t) {
  for (const k of WALKS) {
    if (t < k.t0 || t >= k.t1) continue;
    const u = (t - k.t0) / (k.t1 - k.t0), d = 1 / lerp(1 / k.d0, 1 / k.d1, u), h = clamp(Math.round(1.65 * F / d), 12, 40);
    const spr = WALKER_C[h][Math.floor(t * 2.4) & 1], x = Math.round(PX(d, WALK_L) - spr.width / 2), y = Math.round(PY(d)) - spr.height + 1;
    ctx.globalAlpha = Math.min(1, u / 0.05); ctx.drawImage(spr, x, y); ctx.globalAlpha = 1;
  }
}

/* ================= Saptaparni blossom: cream-white clusters drifting down at three depths ================= */
const BLOSSOM_DEPTHS = [
  { count: 38, nMin: 5, nMax: 8, amp: 2, drift: [-40, 30], land: [177, 186], y0: [60, 150], x: [60, 460] },
  { count: 24, nMin: 7, nMax: 10, amp: 4, drift: [-90, -20], land: [190, 234], y0: [-10, 130], x: [250, 520] },
  { count: 13, nMin: 9, nMax: 12, amp: 7, drift: [-170, -60], land: [H + 8, H + 8], y0: [-12, 110], x: [300, 540] },
];
const BLOSSOMS = BLOSSOM_DEPTHS.map((D, depth) => {
  const r = rng(900 + depth), out = [];
  for (let i = 0; i < D.count; i++) out.push({ id: depth * 100 + i, n: D.nMin + Math.floor(r() * (D.nMax - D.nMin + 1)), ph: r(),
    ks: 24 + Math.floor(r() * 40), p2: r(), kf: 30 + Math.floor(r() * 40), p3: r(), shape: Math.floor(r() * 3) });
  return out;
});
const BLOOM_SPR = [
  [['.c.', 'cyc', '.c.'], ['c.c', '.y.', 'c.c']], [['.cc.', 'cyyc', '.cc.'], ['..c.', 'cyc.', '.ccc']], [['.c..', 'cyc.', '.ccy', '..c.'], ['c..', 'yc.', 'ccy', '.cc']],
];
function drawBlossoms(depth, t) {
  const D = BLOSSOM_DEPTHS[depth];
  for (const b of BLOSSOMS[depth]) {
    const { cyc, u } = cycle(t, b.n, b.ph);
    const x0 = lerp(D.x[0], D.x[1], hash(b.id, cyc, 1)), y0 = lerp(D.y0[0], D.y0[1], hash(b.id, cyc, 2)), land = lerp(D.land[0], D.land[1], hash(b.id, cyc, 3));
    const sw = wave(t, b.ks, b.p2), x = x0 + u * lerp(D.drift[0], D.drift[1], hash(b.id, cyc, 4)) + sw * D.amp, y = y0 + u * (land - y0);
    if (x < -6 || x > W + 6) continue;
    const a = Math.min(1, u / 0.12, D.land[0] < H ? (1 - u) / 0.1 : 1);
    if (depth === 0) { ctx.globalAlpha = a * 0.8; rect(ctx, Math.round(x), Math.round(y), 1, 1, '#e8e4c8'); continue; }
    const fl = Math.cos(TAU * (b.kf * t / LOOP + b.p3)) < 0 ? 1 : 0;
    ctx.globalAlpha = a;
    ctx.drawImage(BLOOM_C[depth - 1 + (b.shape === 2 && depth === 2 ? 1 : 0)][fl], Math.round(x), Math.round(y));
  }
  ctx.globalAlpha = 1;
}
// blossom afloat on the canal, drifting slowly towards us with the aerators' current
const FLOATERS = [];
{ const r = rng(950); for (let k = 0; k < 26; k++) FLOATERS.push({ y: WTOP + 2 + Math.floor(r() * (H - WTOP - 6)), u: r(), k: 1 + Math.floor(r() * 2) }); }
function drawFloaters(t) {
  for (const f of FLOATERS) {
    const sp = WATER_SPANS[f.y];
    if (!sp || sp[1] - sp[0] < 6) continue;
    const x = Math.round(lerp(sp[0] + 2, sp[1] - 3, frac(f.u + f.k * t / LOOP))) + ripple(f.y, t);
    rect(ctx, x, f.y, f.y > 225 ? 2 : 1, 1, '#d8d4b0');
  }
}
/** rings on the canal where drops (or blossom) land */
function drawRings(t, n, heavy) {
  for (let k = 0; k < n; k++) {
    const { cyc, u } = cycle(t, 300 + k * 17, k / n), y = WTOP + 3 + Math.floor(hash(k, cyc, 1) * (H - WTOP - 4)), sp = WATER_SPANS[y];
    if (!sp || sp[1] - sp[0] < 8) continue;
    const x = lerp(sp[0] + 4, sp[1] - 4, hash(k, cyc, 2)), s = (y - HZ) / 40, r = (1 + u * (heavy ? 5 : 3)) * s;
    ctx.globalAlpha = (1 - u) * 0.5; ctx.fillStyle = '#a8b4d8';
    for (let j = 0; j < 10; j++) { const a = j / 10 * TAU; ctx.fillRect(Math.round(x + Math.cos(a) * r), Math.round(y + Math.sin(a) * r * 0.3), 1, 1); }
  }
  ctx.globalAlpha = 1;
}

/* ================= A picked weather (site/js/engine/weather.js) ================= */
// New Delhi draws its own saptaparni blossom (only with its own weather); rain, snow and leaves land on the hexagon's
// lawns, on the canal and the lawns by it, and past the bottom, catching the lamp posts, the kiosk, the gate's floodlights
const WEATHER = weatherPlans(['saptaparni']);
const WX_LAND = [[177, 186], [190, 234], [H + 4, H + 4]];
const WX_LIGHTS = [
  ...[...NEAR_LAMPS, ...FAR_LAMPS].map(l => { const s = F / l.d; return { x: PX(l.d, l.L), y: PY(l.d, l.hgt - 0.3), rx: 2.2 * s, ry: 3 * s }; }),
  ...ROAD_LAMPS.map(l => ({ x: l.x, y: PY(ROAD_LAMP.d, ROAD_LAMP.hgt), rx: 26, ry: 34, col: '#fff0d8' })),
  { x: IG.cx, y: 150, rx: 70, ry: 44, col: '#ffc878' }, { x: KIOSK.cx, y: 168, rx: 22, ry: 14, col: '#e8fff0' },
  { x: PX(BHUTTA.d, BHUTTA.L) - 8, y: 196, rx: 16, ry: 14, col: '#ff9a4a' },
];
const WX = { land: WX_LAND, lights: WX_LIGHTS };
const WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BB.x + BB.w / 2, y: BB.y + BB.h / 2, rx: 34, ry: 22, col: '#ffd0a0' }] };

/* ================= Pre-render everything static ================= */
let HL, SKY_C, HAZE_C, FAR, CAN_C, IG_C, GROUND, WATER_SPANS, WATER_C, MIRROR_C, REF_C, REF_X, ROADL_C, PCR_C, VEH, TRIKE_C, HEDGE_C, BOAT_C,
  JAMUN_C, FARL_C, FAR_GLASS, NEARL_C, NEAR_GLASS, KIOSK_C, SIGN_C, AD, BB_PITCH, BB_GLOW_C, BHUTTA_C, KULFI_C, BALLOON_C, BAL_SPR,
  SAPTA_C, FAMILY_C, COUPLE_C, DOG_C, FARFOLK_C, WALKER_C, BLOOM_C, POOL, VIG_C;
function prerender() {
  HL = {
    strobe: makeHalo(4, '#e8f0ff', 0.6, 1), landing: makeHalo(6, '#fff6d8', 0.5, 1.2),
    cop: COP_COL.map(c => makeHalo(7, c[0], 0.5, 1.3)), copF: makeHalo(4, '#8ad8ff', 0.5, 1),
    ig: makeHalo(120, '#ff9a48', 0.12, 1.4), igMid: makeHalo(60, '#ffc878', 0.12, 1.5), igBase: makeHalo(40, '#ffd08a', 0.2, 1.5),
    flood: makeHalo(6, '#fff0c8', 0.55, 1.2), can: makeHalo(26, '#ffc070', 0.16, 1.4), statue: makeHalo(8, '#ffe0b0', 0.25),
    far: makeHalo(3, '#ffc888', 0.35, 1), aviation: makeHalo(3, '#ff3a2a', 0.6, 1),
    roadLamp: makeHalo(22, '#fff0d0', 0.18, 1.5), roadCore: makeHalo(4, '#ffffff', 0.6, 1),
    head: makeHalo(6, '#fff2d0', 0.45), headCore: makeHalo(2, '#ffffff', 0.7, 1), tail: makeHalo(3, '#ff3a2a', 0.5, 1),
    beaconR: makeHalo(7, '#ff3a3a', 0.6, 1.2), beaconB: makeHalo(7, '#4a7aff', 0.6, 1.2), trike: makeHalo(4, '#fff0d0', 0.35),
    lamp: makeHalo(40, '#ffd49a', 0.16, 1.6), lampCore: makeHalo(8, '#fff4dc', 0.5, 1.3), lampFar: makeHalo(26, '#ffd49a', 0.16, 1.6),
    tube: makeHalo(18, '#e0fff0', 0.22), bbGlow: makeHalo(38, '#ffc8a0', 0.1), bulb: makeHalo(3, '#fff0b0', 0.5, 1),
    coals: makeHalo(18, '#ff7a2a', 0.32, 1.4), led: makeHalo(8, '#c8e8ff', 0.45), ledBal: makeHalo(6, '#ffc8f0', 0.4),
    ears: makeHalo(7, '#ff5ad8', 0.45), top: makeHalo(6, '#ffffff', 0.35),
  };
  SKY_C = renderSky(); HAZE_C = renderHaze(); FAR = renderFar(); CAN_C = renderCanopy(); IG_C = renderIG();
  GROUND = renderGround(); WATER_SPANS = GROUND.water;
  WATER_C = renderWater(WATER_SPANS);
  ROADL_C = renderRoadLamps(); PCR_C = renderPCR(); TRIKE_C = renderTrike(); HEDGE_C = renderHedge();
  const vcache = {};
  VEH = TRAFFIC.map(v => {
    const key = v.type + v.col + v.bus;
    if (vcache[key]) return vcache[key];
    const [b, D] = VPAL.car[v.col];
    let spr;
    if (v.type === 'auto') spr = sprite(AUTO_ROWS, { ...VPAL.auto, L: '#fff4d0', t: '#ff3a2a', k: '#0a080c', o: '#6a6a72' });
    else if (v.type === 'car') spr = sprite(CAR_ROWS, { b, D, w: '#2a3446', L: '#fff4d0', t: '#ff3a2a', k: '#0a080c', o: '#6a6a72' });
    else if (v.type === 'amb') spr = sprite(AMB_ROWS, { a: '#ecece6', A: '#9a9a96', w: '#2a3446', c: '#c8ccd4', L: '#fff4d0', t: '#ff3a2a', k: '#0a080c', o: '#6a6a72' });
    else if (v.type === 'bike') spr = sprite(BIKE_ROWS, { h: '#141010', f: '#a86e48', s: ['#3a5a9a', '#c83a3a', '#e8e0cc'][v.col % 3], b: '#1a1a22', L: '#fff4d0', t: '#ff3a2a', k: '#0a080c', o: '#6a6a72' });
    else spr = renderBus(v.bus);
    return (vcache[key] = spr);
  });
  BOAT_C = BOATS.map(renderBoat);
  JAMUN_C = JAMUNS.map(renderJamun);
  { const p = pix(W, H); FAR_GLASS = FAR_LAMPS.map(l => paintLamp(p, l.d, l.L, l.hgt)); FARL_C = trim(p); }
  NEAR_GLASS = []; NEARL_C = NEAR_LAMPS.map(l => { const p = pix(W, H); NEAR_GLASS.push(paintLamp(p, l.d, l.L, l.hgt)); return trim(p); });
  KIOSK_C = renderKiosk(); SIGN_C = renderSignFrame(); AD = renderAd(); BB_PITCH = makePitch(BB.w, BB.h, { accent: ACCENT });
  { const p = pix(BB.w + 8, BB.h + 8); for (let y = 0; y < BB.h + 8; y++) for (let x = 0; x < BB.w + 8; x++) { const q = Math.min(x, y, BB.w + 7 - x, BB.h + 7 - y) / 5; if (q > bayer(x, y) && hash(x, y >> 1, 960) < 0.55) p.set(x, y, x > 6 && x < 20 ? '#ffb070' : y > 10 && y < 18 ? '#fff0d0' : '#c84a3a'); } BB_GLOW_C = p.done(); }
  BHUTTA_C = renderBhutta(); KULFI_C = renderKulfi(); BALLOON_C = renderBalloonMan();
  BAL_SPR = { round: {}, heart: {} };
  for (const bl of BALLOONS) { BAL_SPR.round[bl.col[0]] ||= makeBalloon(bl.col, false); BAL_SPR.heart[bl.col[0]] ||= makeBalloon(bl.col, true); }
  SAPTA_C = SAPTA.map(renderSapta);
  FAMILY_C = renderFamily(); COUPLE_C = renderCouple(); DOG_C = renderDog(); FARFOLK_C = renderFarFolk();
  WALKER_C = []; for (let h = 12; h <= 40; h++) WALKER_C[h] = [makeWalkers(h, 0), makeWalkers(h, 1)];
  const bpal = { c: '#f2eed4', y: '#c8d078' };
  BLOOM_C = [BLOOM_SPR[1].map(r => sprite(r, bpal)), BLOOM_SPR[0].map(r => sprite(r, bpal)), BLOOM_SPR[2].map(r => sprite(r, bpal))];
  POOL = { coals: makePool(20, 4, '#ff8a3a', 0.22), lampW: makePool(14, 3, '#ffc880', 0.2), bb: makePool(22, 3, '#ffb080', 0.16) };
  VIG_C = renderVignette();
  // the reflection of everything that stands still, each thing about its own line on the water
  const [mc, mx] = makeCanvas(W, H);
  mirrorInto(mx, SKY_C, HZ);
  mirrorInto(mx, FAR.c, mirrorRow(520), FAR.x, FAR.y);
  mirrorInto(mx, CAN_C.c, mirrorRow(CAN.d), CAN_C.x, CAN_C.y);
  mx.globalCompositeOperation = 'lighter'; mx.globalAlpha = 0.9;
  mx.drawImage(HL.igMid.c, Math.round(IG.cx) - HL.igMid.r, Math.round(2 * mirrorRow(IG.d) - 140) - HL.igMid.r);
  mx.globalAlpha = 1; mx.globalCompositeOperation = 'source-over';
  mirrorInto(mx, IG_C.c, mirrorRow(IG.d), IG_C.x, IG_C.y);
  mirrorInto(mx, ROADL_C.c, mirrorRow(ROAD_LAMP.d), ROADL_C.x, ROADL_C.y);
  mirrorInto(mx, KIOSK_C.c, mirrorRow(KIOSK.d), KIOSK_C.x, KIOSK_C.y);
  JAMUNS.forEach((q, i) => mirrorInto(mx, JAMUN_C[i].c, mirrorRow(q.d), JAMUN_C[i].x, JAMUN_C[i].y));
  mx.fillStyle = '#fff0d0';
  for (const x of ROAD_HEADS) mx.fillRect(x - 1, Math.round(2 * mirrorRow(ROAD_LAMP.d) - PY(ROAD_LAMP.d, ROAD_LAMP.hgt)), 3, 2);
  MIRROR_C = mc;
  [REF_C, REF_X] = makeCanvas(W, H);
}
function renderVignette() {
  const p = pix(W, H), c = [8, 4, 14];
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.72, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, c, Math.round(q * 0.42 * 255));
  }
  return p.done();
}

/* ================= Per-frame layers ================= */
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) {
    const a = s.b * (0.6 + 0.4 * wave(t, s.k, s.ph));
    ctx.fillStyle = s.col; ctx.globalAlpha = a; ctx.fillRect(s.x, s.y, 1, 1);
    if (s.bright) { ctx.globalAlpha = a * 0.3; ctx.fillRect(s.x - 1, s.y, 1, 1); ctx.fillRect(s.x + 1, s.y, 1, 1); ctx.fillRect(s.x, s.y - 1, 1, 1); ctx.fillRect(s.x, s.y + 1, 1, 1); }
  }
  ctx.globalAlpha = 0.8;
  const off = Math.floor(frac(t / LOOP) * W);
  ctx.drawImage(HAZE_C, off - W, HAZE_Y); ctx.drawImage(HAZE_C, off, HAZE_Y);
  ctx.globalAlpha = 1;
  drawPlanes(t);
  drawBats(t);
}
function drawFarLayer(t) {
  blit(FAR);
  for (const l of FAR.lights) {
    if (l.id < 100) { if (hash(l.id, Math.floor(frac(t / LOOP + l.id * 0.13) * 5), 3) < 0.7) rect(ctx, l.x, l.y, 1, 1, '#e8c890'); continue; }
    rect(ctx, l.x, l.y, 1, 1, l.warm ? '#ffc070' : '#e8f0ff'); halo(HL.far, l.x, l.y, 0.6 + 0.2 * wave(t, 40 + l.id, 0.1));
  }
  for (const a of AVIATION) if (frac(t / 2 + a.k) < 0.5) { rect(ctx, a.x, a.y, 1, 1, '#ff3a2a'); halo(HL.aviation, a.x, a.y, 0.9); }
}
function drawCanopy(t) {
  halo(HL.can, CAN.cx, CAN.base - 18, 0.9 + 0.05 * wave(t, 5, 0.37));
  blit(CAN_C);
  halo(HL.statue, CAN.cx, CAN.base - 12, 0.7);
}
function drawIG(t) {
  const br = 0.94 + 0.06 * wave(t, 6, 0.3);
  halo(HL.ig, IG.cx, 150, br); halo(HL.igMid, IG.cx, 128, br);
  blit(IG_C);
  halo(HL.igBase, IG.cx, 172, br);
  for (const x of [IG.x0 - 4, IG.x0 + 8, IG.x1 - 8, IG.x1 + 4]) { rect(ctx, Math.round(x), 178, 1, 1, '#fff6d8'); halo(HL.flood, x, 178, 0.8); }
}
function drawRoadLamps(t) {
  blit(ROADL_C);
  const y = Math.round(PY(ROAD_LAMP.d, ROAD_LAMP.hgt));
  ROAD_HEADS.forEach((x, i) => {
    const lv = 0.92 + 0.08 * wave(t, 900 + i * 31, i * 0.1 + 0.13);
    rect(ctx, x - 1, y + 1, 3, 1, '#fff6e0'); halo(HL.roadLamp, x, y + 2, lv); halo(HL.roadCore, x, y + 1, lv);
  });
}
function lampAt(g, t, i, far) {
  const lv = (0.95 + 0.05 * wave(t, 1300 + i * 17, i * 0.2 + 0.07)) * (i === 1 && !far ? flick(t, LAMP_FLICK, 4) : 1);
  drawGlass(g, lv);
  halo(far ? HL.lampFar : HL.lamp, g.cx, g.cy, lv * (far ? 0.9 : 1));
  halo(HL.lampCore, g.cx, g.cy, lv);
  return lv;
}
const LAMP_FLICK = [[83.5, 84.8], [201, 202.2]];
// moths round the nearest lamp
function drawMoths(t, g) {
  for (let k = 0; k < 4; k++) {
    const a = TAU * ((13 + k * 5) * t / LOOP + k * 0.27), r = 5 + k * 2 + Math.sin(TAU * (31 + k) * t / LOOP) * 2;
    rect(ctx, Math.round(g.cx + Math.cos(a) * r), Math.round(g.cy + Math.sin(TAU * (17 + k * 6) * t / LOOP + k) * r * 0.6), 1, 1, '#f0e0c0');
  }
}
function drawKulfi(t) {
  blit(KULFI_C);
  const l = KULFI_C.led, lv = 0.85 + 0.15 * wave(t, 700, 0.21);
  rect(ctx, l.x - 1, l.y - 1, 3, 1, '#e8f4ff'); halo(HL.led, l.x, l.y, lv);
}
function drawBalloonMan(t) {
  blit(BALLOON_C);
  const throwUp = COPTERS.some(c => !c.far && t >= c.t0 - 0.4 && t < c.t0 + 0.3), h = BALLOON_C.hand;
  if (throwUp) { rect(ctx, h.x, h.y - 7, 1, 7, '#a86e48'); rect(ctx, h.x - 1, h.y - 9, 3, 2, '#c8a060'); }   // the slingshot up
  drawBalloons(t);
}

/* ================= Frame compositor (back to front) ================= */
// render(t) paints the chai ad on the kiosk's sign; { pitch } paints the site's faces on it; { billboard: false } leaves
// out the sign, its posts, bulbs, glow and reflection (the loop videos); { weather } draws a WEATHERS pick
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, own = !!wx.saptaparni;
  const wet = wx.shared === 'rain' || wx.shared === 'drizzle' || wx.shared === 'thunderstorm';
  drawSky(t);
  drawFarLayer(t);
  if (own) drawBlossoms(0, t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawCanopy(t);
  drawIG(t);
  blit(GROUND.far);
  drawStrollers(t);
  drawCopters(t, true);
  drawTraffic(t, 0); drawTraffic(t, 1);
  drawPCR(t);
  drawTrike(t);
  drawRoadLamps(t);
  blit(HEDGE_C);
  drawWater(t, billboard);
  if (own) drawFloaters(t);
  if (wet) drawRings(t, wx.shared === 'drizzle' ? 10 : 22, wx.shared !== 'drizzle');
  ctx.drawImage(GROUND.near, 0, 0);
  drawBoats(t);
  if (billboard) drawPool(POOL.bb, BB.x + BB.w / 2, KIOSK.base + 2, 0.9);
  // across the canal, far to near: the jamuns, the kiosk and its sign, the families, the walkers, the lamp posts
  for (const j of JAMUN_C) blit(j);
  drawKiosk(t);
  if (billboard) drawBillboard(t, pitch);
  blit(FARFOLK_C);
  drawWalkers(t);
  blit(FARL_C);
  FAR_GLASS.forEach((g, i) => lampAt(g, t, i, true));
  if (own) drawBlossoms(1, t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  // our side, far to near
  blit(NEARL_C[3]); lampAt(NEAR_GLASS[3], t, 3, false);
  blit(NEARL_C[2]); lampAt(NEAR_GLASS[2], t, 2, false);
  drawKulfi(t);
  blit(SAPTA_C[0]);
  blit(NEARL_C[1]); lampAt(NEAR_GLASS[1], t, 1, false);
  drawBalloonMan(t);
  drawCopters(t, false);
  blit(SAPTA_C[1]);
  blit(NEARL_C[0]); lampAt(NEAR_GLASS[0], t, 0, false);
  drawMoths(t, NEAR_GLASS[0]);
  drawBhutta(t);
  drawCouple(t);
  drawFamily(t);
  drawDog(t);
  if (own) drawBlossoms(2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

const IG_SPAN = [Math.floor(IG.x0 - 0.6 * IG.s), Math.ceil(PX(IG.d + IG.dd + 0.6, AXIS + IG.w / 2 + 0.6))];   // platform to the cornice's far corner

export default {
  id: 'new-delhi',
  name: 'New Delhi',
  tagline: 'October evening at India Gate',
  timeZone: 'Asia/Kolkata',
  accent: '#ffa8a8',                                   // = ACCENT, written out: tools/seo/seo.mjs reads it from the source
  focusX: Math.round(IG.cx),
  landmark: IG_SPAN,                                   // India Gate, the platform's left edge to the south face's cornice
  landmarkTop: IG_TOP,                                 // the row of the bowl's crown, which landscape crops keep
  billboard: BB,
  ambience: { crowd: 0.36, traffic: 0.32, crickets: 0.3, water: 0.06, wind: 0.05 },
  events: [
    ...TRIKE_BELL.map(t => ({ t, type: 'chime', x: 228, gain: 0.45 })),
    ...TRAFFIC.filter(v => v.type === 'bus').map(v => ({ t: +((v.t0 + (W + 8 - 250) / v.v) % LOOP).toFixed(1), type: 'horn', x: 250, gain: 0.22 })),
  ].sort((a, b) => a.t - b.t),
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
